import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow } from 'swiper/modules';
import ColorThief from 'colorthief';
import { getRandomArtists, getTopArtists, getCountryFromLanguage, getCountryFromIP } from '../../services/lastfm';
import SpotifyService from '../../services/spotify';
import { searchSpotifyArtist } from '../../services/spotify';
import { searchDeezerTracks } from '../../services/deezer';
import { searchArtistForProvider } from '../../services/musicProvider';
import ListenModal, { LISTEN_PLATFORMS } from '../ui/ListenModal';
import ArtistCacheService from '../../services/artistCache';
import { supabase } from '../../config/supabase';
import FavoritesService from '../../services/favorites';
import LoginModal from '../ui/LoginModal';
import { getSimilarTracksFromGemini, getFreeTextSearchFromGemini, getFreeTextBridgeMessage } from '../../services/gemini';
import geminiLogger from '../../services/geminiLogger';
import wakeLockService from '../../services/wakeLock';
import AutocompleteInput from '../common/AutocompleteInput';
import FreeTextInput from '../common/FreeTextInput';
import PromptSuggestions from '../ui/PromptSuggestions';
import LoadingSlides from '../ui/LoadingSlides';
import GeminiLoadingScreen from '../ui/GeminiLoadingScreen';
import CustomPagination from '../ui/CustomPagination';
import ArtistCard from '../ui/ArtistCard';
import PageHeader from '../ui/PageHeader';
import ArtistAvatar from '../ui/ArtistAvatar';
import ArtistInfoModal from '../ui/ArtistInfoModal';
import Header from '../layout/Header';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { useApp } from '../../context/AppContext';
import { PersistenceService } from '../../services/persistence';
import { MUSIC_PROVIDER } from '../../config/musicProvider';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import '../../styles/showResult.css';
import '../../styles/artistCard.css';

/** Bridge con typewriter graduale */
const TypedBridgeText = ({ text, className = '' }) => {
    const [typed, setTyped] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        const full = (text || '').replace(/\r\n/g, '\n');
        if (!full) {
            setTyped('');
            setDone(false);
            return undefined;
        }
        let i = 0;
        let active = true;
        let timer = null;
        setTyped('');
        setDone(false);

        const tick = () => {
            if (!active) return;
            i = Math.min(i + 2, full.length);
            setTyped(full.slice(0, i));
            if (i >= full.length) {
                setDone(true);
                return;
            }
            const ch = full.charAt(i - 1);
            let delay = 24;
            if (ch === '.') delay = 110;
            else if (ch === ',') delay = 60;
            else if (ch === '\n') delay = 90;
            else if (ch === ' ') delay = 30;
            timer = setTimeout(tick, delay);
        };
        timer = setTimeout(tick, 40);
        return () => {
            active = false;
            if (timer) clearTimeout(timer);
        };
    }, [text]);

    if (!text) return null;
    return (
        <p className={className}>
            {typed}
            {!done && <span className="typing-cursor">|</span>}
        </p>
    );
};

const getServerErrorMessage = (language) => {
    return language === 'it'
        ? 'Sto riacchiappando il ritmo, riprova tra un attimo.'
        : 'I’m tuning the instruments, give me another try in a moment.';
};

const DEBUG_DATA_LOGS = false;
const debugLog = (...args) => {
    if (DEBUG_DATA_LOGS) {
        console.log(...args);
    }
};
const debugWarn = (...args) => {
    if (DEBUG_DATA_LOGS) {
        console.warn(...args);
    }
};

// Cache in-memory del gradiente per URL immagine (evita ricalcoli ColorThief su slide visitate)
const slideGradientCache = new Map();

const ArtistSlide = ({ artist, isActive, shouldLoad, pageType, position, query, searchType, onSlideReady, onBackToSearch, onOpenListenModal }) => {
    const { language } = useLanguage();
    const { t } = useTranslation(language);
    const { favoriteArtistIds, refreshFavorites, isAuthenticated } = useApp();
    const [background, setBackground] = useState('#121212');
    const [spotifyData, setSpotifyData] = useState(null);
    const [spotifyAlbums, setSpotifyAlbums] = useState([]); // Album (max 6)
    const [deezerTracks, setDeezerTracks] = useState([]);
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
    const [isPlayerLoading, setIsPlayerLoading] = useState(false);
    const [playerError, setPlayerError] = useState(null);
    const [isPlayingPreview, setIsPlayingPreview] = useState(false);
    const [previewTime, setPreviewTime] = useState(0);
    const [previewDuration, setPreviewDuration] = useState(30);
    const [lastfmUrl, setLastfmUrl] = useState(artist?.url || `https://www.last.fm/music/${encodeURIComponent(artist.name)}`);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [spotifyFound, setSpotifyFound] = useState(false); // Traccia se Spotify ha trovato l'artista
    const isLoadingRef = useRef(false); // Evita chiamate duplicate
    const [artistId, setArtistId] = useState(null); // ID artista dal DB
    const [isFavorite, setIsFavorite] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    
    // Refs per scroll orizzontale
    const albumsRef = useRef(null);
    const tracksRef = useRef(null);
    const audioRef = useRef(null);
    const [isScrollingAlbums, setIsScrollingAlbums] = useState(false);
    const hasNotifiedReadyRef = useRef(false);

    const loadDeezerPreviews = async (artistName) => {
        if (!artistName) return;

        setIsPlayerLoading(true);
        setPlayerError(null);

        try {
            const deezerResponse = await searchDeezerTracks(artistName, 30).catch(() => null);
            const deezerResults = deezerResponse?.data || [];

            if (!deezerResults.length) {
                setDeezerTracks([]);
                setCurrentPreviewIndex(0);
                setIsPlayingPreview(false);
                return;
            }

            const normalizedArtist = artistName.toLowerCase();
            const filteredTracks = deezerResults.filter(track => {
                const trackArtist = track?.artist?.name?.toLowerCase() || '';
                return trackArtist.includes(normalizedArtist);
            });

            const safeTracks = (filteredTracks.length > 0 ? filteredTracks : deezerResults).slice(0, 10);
            setDeezerTracks(safeTracks);
            setCurrentPreviewIndex(0);
            setPreviewTime(0);
            setIsPlayingPreview(false);
        } catch (error) {
            console.warn(`[Deezer] Unable to fetch previews for ${artistName}:`, error.message);
            setPlayerError('Impossibile caricare le anteprime');
            setDeezerTracks([]);
            setCurrentPreviewIndex(0);
            setIsPlayingPreview(false);
        } finally {
            setIsPlayerLoading(false);
        }
    };

    const formatTime = (seconds) => {
        if (seconds === undefined || seconds === null || Number.isNaN(seconds)) {
            return '0:00';
        }
        const totalSeconds = Math.max(0, Math.floor(seconds));
        const minutes = Math.floor(totalSeconds / 60);
        const remaining = totalSeconds % 60;
        return `${minutes}:${remaining.toString().padStart(2, '0')}`;
    };

    // Lazy loading: carica quando shouldLoad è true (preload 10 slide prima/dopo)
    // NON richiede isActive per preload, ma carica prioritariamente se isActive
    useEffect(() => {
        setLastfmUrl(artist?.url || `https://www.last.fm/music/${encodeURIComponent(artist.name)}`);
    }, [artist?.url, artist.name]);

    // IMPORTANTE: resetta hasNotifiedReadyRef quando l'artista cambia
    // Questo permette alla nuova slide di notificare quando è pronta
    useEffect(() => {
        hasNotifiedReadyRef.current = false;
        setHasLoaded(false);
        isLoadingRef.current = false;
    }, [artist?.name]);

    const notifySlideReady = () => {
        if (!hasNotifiedReadyRef.current && typeof onSlideReady === 'function') {
            onSlideReady(position);
            hasNotifiedReadyRef.current = true;
        }
    };

    useEffect(() => {
        // NON fare chiamate se:
        // 1. shouldLoad è false (slide non nel range di preload)
        // 2. hasLoaded è true (già caricato)
        // 3. isLoadingRef è true (già in caricamento)
        if (!shouldLoad || hasLoaded || isLoadingRef.current) return;
        
        // Marca come in caricamento per evitare chiamate duplicate
        isLoadingRef.current = true;

        const fetchData = async () => {
            try {
                debugLog(`🔄 Loading Spotify data for: "${artist.name}" (isActive: ${isActive}, shouldLoad: ${shouldLoad})`);
                debugLog(`   Genre hint: ${artist.genre || 'none'}, Origin hint: ${artist.origin || 'none'}`);
                
                const existingSpotifyId = artist.spotifyId || artist.spotify_id || artist.spotifyID || null;

                // Provider attivo (Deezer/Spotify) — stesso adapter del desktop
                const providerArtist = await searchArtistForProvider(
                    artist.name, 
                    existingSpotifyId,
                    artist.genre,
                    artist.origin
                ).catch(e => {
                    debugWarn(`Provider search failed for ${artist.name}:`, e.message);
                    return null;
                });
                
                if (!providerArtist) {
                    debugWarn(`❌ Provider found nothing for: "${artist.name}" - showing fallback card`);
                    setSpotifyFound(false);
                    setHasLoaded(true);
                    notifySlideReady();
                    isLoadingRef.current = false;
                    // Mostra comunque la card con i dati Gemini/LastFM (niente return silenzioso)
                    return;
                }

                debugLog(`✅ Provider found: "${providerArtist.name}" (requested: "${artist.name}")`);
                if (providerArtist.name.toLowerCase() !== artist.name.toLowerCase()) {
                    debugWarn(`⚠️ NAME MISMATCH: Requested "${artist.name}" but got "${providerArtist.name}"`);
                }
                setSpotifyFound(true);
                setSpotifyData(providerArtist);

                setLastfmUrl(artist?.url || `https://www.last.fm/music/${encodeURIComponent(artist.name)}`);

                // Usa gli album già restituiti dalla search (stesso shape normalizzato)
                const validAlbums = (providerArtist.albums || [])
                    .filter(a => {
                        const isValid = a.album_type?.toLowerCase() === 'album' && a.id;
                        if (!isValid && a.album_type?.toLowerCase() === 'album') {
                            debugWarn(`Album senza ID: ${a.name}`, a);
                        }
                        return isValid;
                    })
                    .sort((a, b) => {
                        if (a.popularity !== undefined && b.popularity !== undefined) {
                            return b.popularity - a.popularity;
                        }
                        const dateA = a.release_date || '0000-01-01';
                        const dateB = b.release_date || '0000-01-01';
                        return dateB.localeCompare(dateA);
                    })
                    .slice(0, 6); // Max 6 album
                setSpotifyAlbums(validAlbums);

                setHasLoaded(true);
                notifySlideReady();
                isLoadingRef.current = false;

                // Avvia il caricamento delle preview Deezer in background
                loadDeezerPreviews(artist.name).catch(() => {});
                
            } catch (error) {
                // Handle errors silently
                setHasLoaded(true); // Marca come caricato anche in caso di errore
                notifySlideReady();
                isLoadingRef.current = false;
            }
        };

        fetchData();
        
        // Cleanup: reset isLoadingRef se il componente viene smontato o cambiano le dipendenze
        return () => {
            isLoadingRef.current = false;
        };
    }, [artist.name, shouldLoad, hasLoaded, artist.genre, artist.origin, onSlideReady, position]);

    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) return;

        const handleTimeUpdate = () => {
            setPreviewTime(audioElement.currentTime || 0);
            const durationValue = audioElement.duration;
            if (!Number.isNaN(durationValue) && durationValue) {
                setPreviewDuration(durationValue);
            }
        };

        const handleLoadedMetadata = () => {
            const durationValue = audioElement.duration;
            if (!Number.isNaN(durationValue) && durationValue) {
                setPreviewDuration(durationValue);
            } else {
                setPreviewDuration(30);
            }
        };

        const handleEnded = () => {
            setPreviewTime(0);
            if (deezerTracks.length > 1) {
                setCurrentPreviewIndex(prev => (prev + 1) % deezerTracks.length);
            } else {
                setIsPlayingPreview(false);
            }
        };

        audioElement.addEventListener('timeupdate', handleTimeUpdate);
        audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
        audioElement.addEventListener('ended', handleEnded);

        return () => {
            audioElement.removeEventListener('timeupdate', handleTimeUpdate);
            audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audioElement.removeEventListener('ended', handleEnded);
        };
    }, [deezerTracks.length]);

    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) return;

        const currentTrack = deezerTracks[currentPreviewIndex];

        if (!currentTrack?.preview) {
            audioElement.pause();
                setIsPlayingPreview(false);
                notifySlideReady();
            return;
        }

        audioElement.pause();
        audioElement.src = currentTrack.preview;
        audioElement.load();
        audioElement.currentTime = 0;
        setPreviewTime(0);

        if (isPlayingPreview) {
            const playPromise = audioElement.play();
            if (playPromise?.catch) {
                playPromise.catch(() => setIsPlayingPreview(false));
            }
        }
    }, [deezerTracks, currentPreviewIndex]);

    // Trova artist_id e verifica se è nei preferiti
    useEffect(() => {
        const findArtistId = async () => {
            const spotifyId = artist.spotify_id || artist.spotifyId || spotifyData?.id;
            if (!spotifyId || !isAuthenticated) {
                setArtistId(null);
                setIsFavorite(false);
                return;
            }

            try {
                const { data } = await supabase
                    .from('artists')
                    .select('id')
                    .eq('spotify_id', spotifyId)
                    .single();

                if (data) {
                    setArtistId(data.id);
                    setIsFavorite(favoriteArtistIds.includes(data.id));
                } else {
                    setArtistId(null);
                    setIsFavorite(false);
                }
            } catch (error) {
                setArtistId(null);
                setIsFavorite(false);
            }
        };

        findArtistId();
    }, [artist.spotify_id, artist.spotifyId, spotifyData?.id, favoriteArtistIds, isAuthenticated]);

    useEffect(() => {
        if (!audioRef.current) return;
        if (!isActive) {
            audioRef.current.pause();
            setIsPlayingPreview(false);
            setPreviewTime(0);
        }
    }, [isActive]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, []);

    const displayData = spotifyData || artist;
    const fallbackAlbums = Array.isArray(displayData?.albums)
        ? displayData.albums
        : (Array.isArray(artist?.albums) ? artist.albums : []);
    const albumsToDisplay = spotifyAlbums.length > 0 ? spotifyAlbums : fallbackAlbums;
    
    // Immagine: provider (Deezer/Spotify) oppure fallback dai dati già presenti
    let imageUrl = null;
    if (spotifyData?.images?.[0]?.url) {
        imageUrl = spotifyData.images[0].url;
    } else if (artist?.image_url) {
        imageUrl = artist.image_url;
    } else if (artist?.images?.[0]?.url) {
        imageUrl = artist.images[0].url;
    } else if (artist?.image?.[0]?.['#text']) {
        imageUrl = artist.image[0]['#text'];
    }

    useEffect(() => {
        if (imageUrl) {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = imageUrl;
            
            img.onload = () => {
                try {
                    const colorThief = new ColorThief();
                    const [r, g, b] = colorThief.getColor(img);
                    setBackground(`linear-gradient(180deg, rgb(${r},${g},${b}) 0%, #121212 100%)`);
                } catch (error) {
                    // Handle color extraction errors silently
                    setBackground('#121212');
                }
            };
            
            img.onerror = () => {
                setBackground('#121212');
            };
        }
    }, [imageUrl]);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [menuOpen]);

    // Durante il caricamento mostra subito nome + placeholder (niente slide vuota)
    if (!hasLoaded && !spotifyData) {
        return (
            <div className="artist-slide artist-slide--result">
                <div className="artist-slide-background" style={{ background: '#121212', opacity: isActive ? 1 : 0 }} />
                <div className="artist-slide-overlay" />
                <div className="artist-content">
                    <div className="artist-image-container" style={{ width: '150px', height: '150px', margin: '0 auto', background: '#2a2a2a', borderRadius: '8px' }} />
                    <h2 className="artist-name">{artist.name || '…'}</h2>
                    <p className="artist-genres" style={{ opacity: 0.6 }}>
                        {language === 'it' ? 'Caricamento…' : 'Loading…'}
                    </p>
                </div>
            </div>
        );
    }

    // Funzione per condividere artista (scegli piattaforma)
    const listenIds = {
        deezerId: MUSIC_PROVIDER === 'deezer'
            ? (spotifyData?.id || artist?.deezer_id || null)
            : (artist?.deezer_id || null),
        spotifyId: MUSIC_PROVIDER === 'spotify'
            ? (spotifyData?.id || artist?.spotify_id || artist?.spotifyId || null)
            : (artist?.spotify_id || artist?.spotifyId || null),
        spotifyUrl: displayData?.external_urls?.spotify || null,
    };

    const openListen = (albumName = null, mode = 'listen') => {
        onOpenListenModal?.(
            displayData?.name || artist.name,
            albumName,
            listenIds,
            mode
        );
    };

    const handleShare = () => {
        openListen(null, 'share');
    };

    // Funzione per toggle preferito
    const handleToggleFavorite = async () => {
        if (!isAuthenticated) {
            // Se non loggato, mostra modal di login
            setShowLoginModal(true);
            return;
        }

        const providerId = MUSIC_PROVIDER === 'deezer'
            ? (spotifyData?.id || artist?.deezer_id)
            : (artist.spotify_id || artist.spotifyId || spotifyData?.id);
        if (!providerId) return;

        // Aggiornamento ottimistico: aggiorna subito la UI
        const wasFavorite = isFavorite;
        setIsFavorite(!wasFavorite);

        try {
            const artistName = displayData.name || artist.name;
            const imageUrl = displayData.images?.[0]?.url || spotifyData?.images?.[0]?.url;
            const genres = displayData.genres || spotifyData?.genres || [];

            const payload = MUSIC_PROVIDER === 'deezer'
                ? {
                    deezer_id: providerId,
                    name: artistName,
                    image_url: imageUrl,
                    genres,
                }
                : {
                    spotify_id: providerId,
                    name: artistName,
                    image_url: imageUrl,
                    spotify_url: displayData.external_urls?.spotify || spotifyData?.external_urls?.spotify,
                    genres,
                };

            await FavoritesService.toggleFavorite(payload);

            // Aggiorna i preferiti nel context (in background, non blocca)
            refreshFavorites().catch(err => console.warn('[ArtistSlide] Error refreshing favorites:', err));
        } catch (error) {
            // Se fallisce, revert lo stato
            console.error('[ArtistSlide] Error toggling favorite:', error);
            setIsFavorite(wasFavorite);
        }
    };

    const handleTogglePlay = () => {
        if (!deezerTracks.length) return;
        const audioElement = audioRef.current;
        if (!audioElement) return;

        if (isPlayingPreview) {
            audioElement.pause();
            setIsPlayingPreview(false);
            return;
        }

        const playPromise = audioElement.play();
        if (playPromise?.then) {
            playPromise
                .then(() => {
                    setIsPlayingPreview(true);
                })
                .catch(() => {
                    setIsPlayingPreview(false);
                });
        } else {
            setIsPlayingPreview(true);
        }
    };

    const handleNextTrack = () => {
        if (!deezerTracks.length) return;
        setCurrentPreviewIndex(prev => (prev + 1) % deezerTracks.length);
    };

    const handlePreviousTrack = () => {
        if (!deezerTracks.length) return;
        setCurrentPreviewIndex(prev => (prev - 1 + deezerTracks.length) % deezerTracks.length);
    };

    const currentPreviewTrack = deezerTracks[currentPreviewIndex] || null;
    const previewProgress = previewDuration ? Math.min((previewTime / previewDuration) * 100, 100) : 0;
    const previewCountdown = Math.max((previewDuration || 30) - previewTime, 0);

    return (
        <div className="artist-slide artist-slide--result">
            {/* Pulsante Condividi - ogni slide ha la sua icona con i suoi dati */}
            <button
                onClick={handleShare}
                style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    zIndex: 1000,
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.8)',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                }}
                aria-label={language === 'it' ? 'Condividi artista' : 'Share artist'}
            >
                <span className="material-icons" style={{ fontSize: '20px' }}>share</span>
            </button>

            {/* Pulsante Preferiti (stellina) - sempre visibile, sotto Condividi */}
            <button
                onClick={handleToggleFavorite}
                style={{
                    position: 'absolute',
                    top: '60px',
                    left: '20px',
                    zIndex: 1000,
                    background: 'transparent',
                    border: 'none',
                    color: isFavorite ? '#FFD700' : 'rgba(255, 255, 255, 0.8)',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'opacity 0.2s, color 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                }}
                aria-label={language === 'it' ? (isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti') : (isFavorite ? 'Remove from favorites' : 'Add to favorites')}
            >
                <span className="material-icons" style={{ fontSize: '20px' }}>
                    {isFavorite ? 'star' : 'star_border'}
                </span>
            </button>

            {/* Ascolta su — tre puntini sotto la stella, a sinistra */}
            <div
                ref={menuRef}
                style={{
                    position: 'absolute',
                    top: '100px',
                    left: '20px',
                    zIndex: 1100,
                }}
            >
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen((v) => !v);
                    }}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.9)',
                        cursor: 'pointer',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    aria-label={language === 'it' ? 'Ascolta su' : 'Listen on'}
                >
                    <span className="material-icons" style={{ fontSize: '24px' }}>more_vert</span>
                </button>
                {menuOpen && (
                    <div className="artist-card__menu" style={{ top: '28px', left: 0, right: 'auto' }}>
                        {LISTEN_PLATFORMS.map((platform) => {
                            const url = platform.getUrl(displayData?.name || artist.name, listenIds);
                            return (
                                <a
                                    key={platform.id}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    <platform.Icon />
                                    <span>{platform.name}</span>
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal login per preferiti */}
            <LoginModal 
                isOpen={showLoginModal} 
                onClose={() => setShowLoginModal(false)} 
            />
            
            <div 
                className="artist-slide-background"
                style={{ 
                    background: background,
                    opacity: isActive ? 1 : 0
                }}
            />
            <div className="artist-slide-overlay" />

            <div className="artist-content">
                <div className="relative">
                    <div
                        className="artist-image-container"
                        style={{ width: '150px', height: '150px', margin: '0 auto', position: 'relative' }}
                    >
                        {imageUrl ? (
                            <img 
                                src={imageUrl}
                                alt={artist.name}
                                className="artist-image"
                                onError={() => {
                                    setBackground('#121212');
                                }}
                            />
                        ) : (
                            <div 
                                className="artist-image-placeholder"
                                style={{
                                    backgroundColor: '#000000',
                                    width: '150px',
                                    height: '150px',
                                    borderRadius: '8px'
                                }}
                            />
                        )}
                    </div>
                </div>

                <h2 className="artist-name">{artist.name || 'Unknown Artist'}</h2>
                
                {/* Generi */}
                {(displayData.genres?.length > 0 || artist.genres) && (
                    <p className="artist-genres">
                        {displayData.genres?.slice(0, 3).join(', ') || artist.genres}
                    </p>
                )}
                
                {/* Mostra rank solo per top-artists, NON per daily-discovery-artists */}
                {artist.rank && pageType !== 'daily-discovery-artists' && (
                    <div className="artist-match" style={{ color: '#1db954', marginBottom: '8px' }}>
                        #{artist.rank} in chart
                    </div>
                )}

                {/* Album (max 6) - in quadrati, con scroll */}
                {albumsToDisplay.length > 0 && (
                    <div className="albums-container" style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                        <h3 className="albums-title" style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Top albums</h3>
                        <div 
                            className="albums-scroll"
                            ref={albumsRef}
                            onTouchStart={(e) => {
                                // Salva la posizione iniziale del touch
                                const touch = e.touches[0];
                                if (touch) {
                                    e.currentTarget._startTouch = { x: touch.clientX, y: touch.clientY };
                                }
                                setIsScrollingAlbums(true);
                            }}
                            onTouchMove={(e) => {
                                // Calcola la direzione dello swipe
                                const touch = e.touches[0];
                                if (touch && e.currentTarget._startTouch) {
                                    const deltaX = Math.abs(touch.clientX - e.currentTarget._startTouch.x);
                                    const deltaY = Math.abs(touch.clientY - e.currentTarget._startTouch.y);
                                    
                                    // Se è principalmente orizzontale (scroll album), blocca lo swiper
                                    if (deltaX > deltaY) {
                                        e.stopPropagation();
                                    }
                                } else if (touch) {
                                    // Salva la posizione iniziale se non c'è
                                    e.currentTarget._startTouch = { x: touch.clientX, y: touch.clientY };
                                }
                            }}
                            onTouchEnd={(e) => {
                                // Pulisci i dati del touch
                                if (e.currentTarget._startTouch) {
                                    delete e.currentTarget._startTouch;
                                }
                                setTimeout(() => {
                                    setIsScrollingAlbums(false);
                                }, 100);
                            }}
                            style={{
                                display: 'flex',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                WebkitOverflowScrolling: 'touch',
                                scrollBehavior: 'smooth',
                                gap: '0.75rem',
                                paddingBottom: '0.5rem'
                            }}
                        >
                            {albumsToDisplay.slice(0, 6).map((album, index) => {
                                return (
                                <button
                                    type="button"
                                    key={`${spotifyData?.id || artist.name}-album-${album.id || album.mbid || index}`}
                                    onClick={() => openListen(album.name, 'listen')}
                                    className="album-item"
                                    title={language === 'it' ? `Ascolta ${album.name}` : `Listen to ${album.name}`}
                                    style={{
                                        minWidth: '70px',
                                        flexShrink: 0,
                                        background: 'transparent',
                                        border: 'none',
                                        padding: 0,
                                        color: 'inherit',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <div className="album-image-container" style={{
                                        width: '70px',
                                        height: '70px',
                                        borderRadius: '4px',
                                        position: 'relative'
                                    }}>
                                        {(album.images?.[0]?.url || album.image?.find(img => img.size === 'large')?.['#text'] || album.cover_medium) ? (
                                            <img
                                                src={album.images?.[0]?.url || album.image?.find(img => img.size === 'large')?.['#text'] || album.cover_medium}
                                                alt={album.name}
                                                className="album-image"
                                                style={{ borderRadius: '4px' }}
                                            />
                                        ) : (
                                            <div className="album-placeholder" style={{
                                                backgroundColor: '#2a2a2a',
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                            }}>
                                                <i className="bi bi-music-note" style={{ fontSize: '16px', color: '#666' }} />
                                            </div>
                                        )}
                                        <div className="album-overlay">
                                            <i className="bi bi-play-circle-fill" style={{ fontSize: '20px', color: 'white' }} />
                                        </div>
                                    </div>
                                    <p className="album-name" style={{
                                        fontSize: '0.65rem',
                                        width: '70px',
                                        marginTop: '4px'
                                    }}>{album.name || 'Untitled Album'}</p>
                                </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="deezer-player" ref={tracksRef}>
                    <h3 className="albums-title deezer-player__heading">Preview player</h3>

                    {isPlayerLoading ? (
                        <p className="deezer-player__status">Caricamento anteprime…</p>
                    ) : playerError ? (
                        <p className="deezer-player__status deezer-player__status--error">{playerError}</p>
                    ) : currentPreviewTrack ? (
                        <>
                            <div className="deezer-player__track">
                                <div className="deezer-player__artwork">
                                    {currentPreviewTrack.album?.cover_medium ? (
                                        <img
                                            src={currentPreviewTrack.album.cover_medium}
                                            alt={currentPreviewTrack.title}
                                        />
                                    ) : (
                                        <div className="deezer-player__artwork-placeholder">
                                            <span className="material-icons">music_note</span>
                                        </div>
                                    )}
                                </div>
                                <div className="deezer-player__meta">
                                    <p className="deezer-player__title">{currentPreviewTrack.title}</p>
                                    <p className="deezer-player__artist">{currentPreviewTrack.artist?.name || artist.name}</p>
                                </div>
                                <div className="deezer-player__controls">
                                    <button
                                        className="deezer-player__button"
                                        type="button"
                                        onClick={handlePreviousTrack}
                                        aria-label="Anteprima precedente"
                                    >
                                        <span className="material-icons">skip_previous</span>
                                    </button>
                                    <button
                                        className={`deezer-player__button deezer-player__button--play ${isPlayingPreview ? 'is-playing' : ''}`}
                                        type="button"
                                        onClick={handleTogglePlay}
                                        aria-label={isPlayingPreview ? 'Metti in pausa' : 'Riproduci anteprima'}
                                    >
                                        <span className="material-icons">{isPlayingPreview ? 'pause' : 'play_arrow'}</span>
                                    </button>
                                    <button
                                        className="deezer-player__button"
                                        type="button"
                                        onClick={handleNextTrack}
                                        aria-label="Anteprima successiva"
                                    >
                                        <span className="material-icons">skip_next</span>
                                    </button>
                                </div>
                            </div>

                            <div className="deezer-player__progress">
                                <div className="deezer-player__progress-bar">
                                    <span style={{ width: `${previewProgress}%` }} />
                                </div>
                                <span className="deezer-player__countdown">{formatTime(previewCountdown)}</span>
                            </div>
                        </>
                    ) : (
                        <p className="deezer-player__status">{t('previewPlayer.noPreview')}</p>
                    )}

                    <audio ref={audioRef} preload="none" />
                </div>

                {/*
                {spotifySingles.length > 0 && (
                    <div className="albums-container" style={{ marginTop: '1.5rem' }}>
                        ...
                    </div>
                )}
                */}

                {/* Link a Spotify e LastFM - sempre visibili - più vicino alle tracce */}
                {/* Pulsanti esterni disabilitati temporaneamente */}
            </div>
        </div>
    );
};


const AlbumSlide = ({ album, isActive, shouldLoad, pageType, position, query, searchType }) => {
    const [background, setBackground] = useState('#121212');
    const [spotifyData, setSpotifyData] = useState(null);
    const [artistData, setArtistData] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isSingle, setIsSingle] = useState(false); // Indica se è un singolo invece di un album

    // Lazy loading: carica solo quando shouldLoad è true
    useEffect(() => {
        if (!shouldLoad || hasLoaded) return;

        const fetchData = async () => {
            try {
                debugLog(`🔄 Loading Spotify data for album: ${album.album} by ${album.artist}`);
                
                // 1. Cerca l'album su Spotify con query più precisa (come ArtistSlide)
                let albumResponse = await SpotifyService.request('/search', {
                    params: {
                        q: `album:"${album.album}" artist:"${album.artist}"`,
                        type: 'album',
                        limit: 10 // Più risultati per filtrare meglio
                    }
                }).catch(e => {
                    debugWarn(`Spotify album search failed:`, e.message);
                    return null;
                });

                // Se non trovato, prova una ricerca più semplice
                if (!albumResponse?.albums?.items?.length) {
                    albumResponse = await SpotifyService.request('/search', {
                        params: {
                            q: `${album.album} ${album.artist}`,
                            type: 'album',
                            limit: 10
                        }
                    }).catch(() => null);
                }

                // Filtra i risultati per trovare il match migliore - ESCLUDI SINGOLI
                let spotifyAlbum = null;
                if (albumResponse?.albums?.items?.length) {
                    // Filtra per escludere singoli, compilazioni e soundtrack
                    const validAlbums = albumResponse.albums.items.filter(a => {
                        const albumType = a.album_type?.toLowerCase() || '';
                        // Escludi singoli, compilazioni e soundtrack
                        return albumType === 'album';
                    });
                    
                    // Se non ci sono album validi, prova con tutti i risultati (fallback)
                    const albumsToSearch = validAlbums.length > 0 ? validAlbums : albumResponse.albums.items;
                    
                    // Cerca match esatto per nome album e artista (case-insensitive)
                    const exactMatch = albumsToSearch.find(a => {
                        const albumName = a.name.toLowerCase().trim();
                        const artistName = a.artists?.[0]?.name?.toLowerCase().trim() || '';
                        const searchAlbumName = album.album.toLowerCase().trim();
                        const searchArtistName = album.artist.toLowerCase().trim();
                        
                        // Match esatto o molto simile (tolleranza per caratteri speciali)
                        const albumMatches = albumName === searchAlbumName || 
                                           albumName.includes(searchAlbumName) || 
                                           searchAlbumName.includes(albumName);
                        const artistMatches = artistName === searchArtistName ||
                                             artistName.includes(searchArtistName) ||
                                             searchArtistName.includes(artistName);
                        
                        return albumMatches && artistMatches;
                    });
                    
                    // Se non c'è match esatto, cerca match parziale (come prima)
                    if (!exactMatch) {
                        const partialMatch = albumsToSearch.find(a => {
                            const albumName = a.name.toLowerCase().trim();
                            const artistName = a.artists?.[0]?.name?.toLowerCase().trim() || '';
                            const searchAlbumName = album.album.toLowerCase().trim();
                            const searchArtistName = album.artist.toLowerCase().trim();
                            
                            // Album simile E artista uguale
                            return (albumName.includes(searchAlbumName) || searchAlbumName.includes(albumName)) &&
                                   artistName === searchArtistName;
                        });
                        // NON usare il primo risultato se non corrisponde - verifichiamo prima se l'artista esiste
                        spotifyAlbum = partialMatch || null;
                    } else {
                        spotifyAlbum = exactMatch;
                    }
                }

                if (spotifyAlbum) {
                    setSpotifyData({
                        ...spotifyAlbum,
                        external_urls: spotifyAlbum.external_urls,
                        artists: spotifyAlbum.artists
                    });
                    
                    // Imposta se è un singolo o un album
                    setIsSingle(spotifyAlbum.album_type?.toLowerCase() === 'single');

                    // 2. Cerca l'artista per avere l'immagine dell'artista (come ArtistSlide)
                    if (spotifyAlbum.artists?.[0]?.id) {
                        const artistResponse = await SpotifyService.request(`/artists/${spotifyAlbum.artists[0].id}`).catch(() => null);
                        if (artistResponse) {
                            setArtistData(artistResponse);
                        }
                    }
                    
                    // Log confronto Gemini vs Spotify - SEMPLIFICATO
                    const spotifyAlbumName = spotifyAlbum.name || '';
                    const spotifyArtistName = spotifyAlbum.artists?.[0]?.name || '';
                    const geminiAlbumName = album.album || '';
                    const geminiArtistName = album.artist || '';
                    const albumMatches = spotifyAlbumName.toLowerCase().trim() === geminiAlbumName.toLowerCase().trim();
                    const artistMatches = spotifyArtistName.toLowerCase().trim() === geminiArtistName.toLowerCase().trim();
                    const isMatch = albumMatches && artistMatches;
                    
                    // Se l'artista corrisponde ma l'album no, cerca il miglior album dell'artista
                    if (artistMatches && !albumMatches && spotifyAlbum.artists?.[0]?.id) {
                        try {
                            // Ottieni gli album dell'artista
                            const artistAlbumsResponse = await SpotifyService.request(`/artists/${spotifyAlbum.artists[0].id}/albums`, {
                                params: {
                                    include_groups: 'album',
                                    limit: 10,
                                    market: 'IT'
                                }
                            }).catch(() => null);
                            
                            if (artistAlbumsResponse?.items?.length > 0) {
                                const validArtistAlbums = artistAlbumsResponse.items.filter(a => 
                                    a.album_type?.toLowerCase() === 'album'
                                );
                                
                                if (validArtistAlbums.length > 0) {
                                    const bestAlbum = validArtistAlbums[0];
                                    
                                    // Sostituisci con il miglior album dell'artista
                                    spotifyAlbum = bestAlbum;
                                    setSpotifyData({
                                        ...bestAlbum,
                                        external_urls: bestAlbum.external_urls,
                                        artists: bestAlbum.artists
                                    });
                                    setIsSingle(false); // È un album
                                    
                                    // Log semplice: mismatch + album selezionato
                                    debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → MISMATCH → Album selezionato: "${bestAlbum.name}"`);
                                    
                                    geminiLogger.logGeminiSpotifyComparison({
                                        query: query || 'unknown',
                                        searchType: searchType || 'album',
                                        position: position || 0,
                                        geminiAlbum: geminiAlbumName,
                                        geminiArtist: geminiArtistName,
                                        geminiMatch: album.match || '0.8',
                                        spotifyFound: true,
                                        spotifyAlbum: bestAlbum.name,
                                        spotifyArtist: spotifyArtistName,
                                        mismatch: false,
                                        fixedWithArtistAlbum: true
                                    });
                                } else {
                                    // Nessun album valido - cerca singoli
                                    const validSingles = artistAlbumsResponse.items.filter(a => 
                                        a.album_type?.toLowerCase() === 'single'
                                    );
                                    
                                    if (validSingles.length > 0) {
                                        const bestSingle = validSingles[0];
                                        
                                        // Usa il singolo più famoso
                                        spotifyAlbum = bestSingle;
                                        setSpotifyData({
                                            ...bestSingle,
                                            external_urls: bestSingle.external_urls,
                                            artists: bestSingle.artists
                                        });
                                        setIsSingle(true); // È un singolo
                                        
                                        debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → MISMATCH → Nessun album, selezionato singolo: "${bestSingle.name}"`);
                                        
                                        geminiLogger.logGeminiSpotifyComparison({
                                            query: query || 'unknown',
                                            searchType: searchType || 'album',
                                            position: position || 0,
                                            geminiAlbum: geminiAlbumName,
                                            geminiArtist: geminiArtistName,
                                            geminiMatch: album.match || '0.8',
                                            spotifyFound: true,
                                            spotifyAlbum: bestSingle.name,
                                            spotifyArtist: spotifyArtistName,
                                            mismatch: false,
                                            fixedWithArtistAlbum: true,
                                            isSingle: true
                                        });
                                    } else {
                                        debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → MISMATCH → Nessun album valido trovato`);
                                        geminiLogger.logGeminiSpotifyComparison({
                                            query: query || 'unknown',
                                            searchType: searchType || 'album',
                                            position: position || 0,
                                            geminiAlbum: geminiAlbumName,
                                            geminiArtist: geminiArtistName,
                                            geminiMatch: album.match || '0.8',
                                            spotifyFound: true,
                                            spotifyAlbum: spotifyAlbumName,
                                            spotifyArtist: spotifyArtistName,
                                            mismatch: true
                                        });
                                    }
                                }
                            } else {
                                debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → MISMATCH → Nessun album trovato per l'artista`);
                                geminiLogger.logGeminiSpotifyComparison({
                                    query: query || 'unknown',
                                    searchType: searchType || 'album',
                                    position: position || 0,
                                    geminiAlbum: geminiAlbumName,
                                    geminiArtist: geminiArtistName,
                                    geminiMatch: album.match || '0.8',
                                    spotifyFound: true,
                                    spotifyAlbum: spotifyAlbumName,
                                    spotifyArtist: spotifyArtistName,
                                    mismatch: true
                                });
                            }
                        } catch (error) {
                            debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → MISMATCH → Errore recupero album`);
                            geminiLogger.logGeminiSpotifyComparison({
                                query: query || 'unknown',
                                searchType: searchType || 'album',
                                position: position || 0,
                                geminiAlbum: geminiAlbumName,
                                geminiArtist: geminiArtistName,
                                geminiMatch: album.match || '0.8',
                                spotifyFound: true,
                                spotifyAlbum: spotifyAlbumName,
                                spotifyArtist: spotifyArtistName,
                                mismatch: true
                            });
                        }
                    } else if (isMatch) {
                        // Match completo
                        debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ✅ MATCH`);
                        geminiLogger.logGeminiSpotifyComparison({
                            query: query || 'unknown',
                            searchType: searchType || 'album',
                            position: position || 0,
                            geminiAlbum: geminiAlbumName,
                            geminiArtist: geminiArtistName,
                            geminiMatch: album.match || '0.8',
                            spotifyFound: true,
                            spotifyAlbum: spotifyAlbumName,
                            spotifyArtist: spotifyArtistName,
                            mismatch: false
                        });
                    } else {
                        // Artista non corrisponde - cerca l'artista Gemini su Spotify e prendi il suo miglior album
                        debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → MISMATCH (artista diverso) → Cercando "${geminiArtistName}" su Spotify...`);
                        
                        try {
                            // Cerca l'artista Gemini su Spotify
                            const { searchSpotifyArtist } = await import('../../services/spotify');
                            const spotifyArtistGemini = await searchSpotifyArtist(
                                geminiArtistName,
                                null,
                                album.genre || '',
                                null
                            ).catch(() => null);
                            
                            if (spotifyArtistGemini && spotifyArtistGemini.id) {
                                // Artista trovato! Ottieni i suoi album
                                const artistAlbumsResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}/albums`, {
                                    params: {
                                        include_groups: 'album',
                                        limit: 10,
                                        market: 'IT'
                                    }
                                }).catch(() => null);
                                
                                if (artistAlbumsResponse?.items?.length > 0) {
                                    const validArtistAlbums = artistAlbumsResponse.items.filter(a => 
                                        a.album_type?.toLowerCase() === 'album'
                                    );
                                    
                                    if (validArtistAlbums.length > 0) {
                                        const bestAlbum = validArtistAlbums[0];
                                        
                                        // Sostituisci con l'album dell'artista corretto
                                        spotifyAlbum = bestAlbum;
                                        setSpotifyData({
                                            ...bestAlbum,
                                            external_urls: bestAlbum.external_urls,
                                            artists: bestAlbum.artists
                                        });
                                        setIsSingle(false); // È un album
                                        
                                        // Cerca anche i dati dell'artista per l'immagine
                                        const artistResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}`).catch(() => null);
                                        if (artistResponse) {
                                            setArtistData(artistResponse);
                                        }
                                        
                                        debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ✅ Artista trovato → Album selezionato: "${bestAlbum.name}"`);
                                        
                                        geminiLogger.logGeminiSpotifyComparison({
                                            query: query || 'unknown',
                                            searchType: searchType || 'album',
                                            position: position || 0,
                                            geminiAlbum: geminiAlbumName,
                                            geminiArtist: geminiArtistName,
                                            geminiMatch: album.match || '0.8',
                                            spotifyFound: true,
                                            spotifyAlbum: bestAlbum.name,
                                            spotifyArtist: spotifyArtistGemini.name,
                                            mismatch: false,
                                            fixedWithArtistAlbum: true,
                                            originalSpotifyArtist: spotifyArtistName // Per tracciare cosa aveva trovato prima
                                        });
                                    } else {
                                        // Nessun album valido - cerca singoli
                                        const validSingles = artistAlbumsResponse.items.filter(a => 
                                            a.album_type?.toLowerCase() === 'single'
                                        );
                                        
                                        if (validSingles.length > 0) {
                                            const bestSingle = validSingles[0];
                                            
                                            // Usa il singolo più famoso
                                            spotifyAlbum = bestSingle;
                                            setSpotifyData({
                                                ...bestSingle,
                                                external_urls: bestSingle.external_urls,
                                                artists: bestSingle.artists
                                            });
                                            setIsSingle(true); // È un singolo
                                            
                                            // Cerca anche i dati dell'artista per l'immagine
                                            const artistResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}`).catch(() => null);
                                            if (artistResponse) {
                                                setArtistData(artistResponse);
                                            }
                                            
                                            debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ✅ Artista trovato → Nessun album, selezionato singolo: "${bestSingle.name}"`);
                                            
                                            geminiLogger.logGeminiSpotifyComparison({
                                                query: query || 'unknown',
                                                searchType: searchType || 'album',
                                                position: position || 0,
                                                geminiAlbum: geminiAlbumName,
                                                geminiArtist: geminiArtistName,
                                                geminiMatch: album.match || '0.8',
                                                spotifyFound: true,
                                                spotifyAlbum: bestSingle.name,
                                                spotifyArtist: spotifyArtistGemini.name,
                                                mismatch: false,
                                                fixedWithArtistAlbum: true,
                                                isSingle: true,
                                                originalSpotifyArtist: spotifyArtistName
                                            });
                                        } else {
                                            // Artista trovato ma nessun album valido
                                            debugWarn(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ⚠️ Artista trovato ma nessun album valido`);
                                            geminiLogger.logGeminiSpotifyComparison({
                                                query: query || 'unknown',
                                                searchType: searchType || 'album',
                                                position: position || 0,
                                                geminiAlbum: geminiAlbumName,
                                                geminiArtist: geminiArtistName,
                                                geminiMatch: album.match || '0.8',
                                                spotifyFound: false,
                                                mismatch: false
                                            });
                                        }
                                    }
                                } else {
                                    // Nessun album - cerca singoli
                                    const singlesResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}/albums`, {
                                        params: {
                                            include_groups: 'single',
                                            limit: 10,
                                            market: 'IT'
                                        }
                                    }).catch(() => null);
                                    
                                    if (singlesResponse?.items?.length > 0) {
                                        const bestSingle = singlesResponse.items[0];
                                        
                                        // Usa il singolo più famoso
                                        spotifyAlbum = bestSingle;
                                        setSpotifyData({
                                            ...bestSingle,
                                            external_urls: bestSingle.external_urls,
                                            artists: bestSingle.artists
                                        });
                                        setIsSingle(true); // È un singolo
                                        
                                        // Cerca anche i dati dell'artista per l'immagine
                                        const artistResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}`).catch(() => null);
                                        if (artistResponse) {
                                            setArtistData(artistResponse);
                                        }
                                        
                                        debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ✅ Artista trovato → Nessun album, selezionato singolo: "${bestSingle.name}"`);
                                        
                                        geminiLogger.logGeminiSpotifyComparison({
                                            query: query || 'unknown',
                                            searchType: searchType || 'album',
                                            position: position || 0,
                                            geminiAlbum: geminiAlbumName,
                                            geminiArtist: geminiArtistName,
                                            geminiMatch: album.match || '0.8',
                                            spotifyFound: true,
                                            spotifyAlbum: bestSingle.name,
                                            spotifyArtist: spotifyArtistGemini.name,
                                            mismatch: false,
                                            fixedWithArtistAlbum: true,
                                            isSingle: true
                                        });
                                    } else {
                                        // Artista trovato ma nessun album né singoli
                                        debugWarn(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ⚠️ Artista trovato ma nessun album né singoli`);
                                        geminiLogger.logGeminiSpotifyComparison({
                                            query: query || 'unknown',
                                            searchType: searchType || 'album',
                                            position: position || 0,
                                            geminiAlbum: geminiAlbumName,
                                            geminiArtist: geminiArtistName,
                                            geminiMatch: album.match || '0.8',
                                            spotifyFound: false,
                                            mismatch: false
                                        });
                                    }
                                }
                            } else {
                                // Artista NON trovato su Spotify - non inventare nulla
                                debugWarn(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ❌ Artista non trovato su Spotify`);
                                geminiLogger.logGeminiSpotifyComparison({
                                    query: query || 'unknown',
                                    searchType: searchType || 'album',
                                    position: position || 0,
                                    geminiAlbum: geminiAlbumName,
                                    geminiArtist: geminiArtistName,
                                    geminiMatch: album.match || '0.8',
                                    spotifyFound: false,
                                    mismatch: false
                                });
                            }
                        } catch (error) {
                            debugWarn(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ❌ Errore ricerca artista: ${error.message}`);
                            geminiLogger.logGeminiSpotifyComparison({
                                query: query || 'unknown',
                                searchType: searchType || 'album',
                                position: position || 0,
                                geminiAlbum: geminiAlbumName,
                                geminiArtist: geminiArtistName,
                                geminiMatch: album.match || '0.8',
                                spotifyFound: false,
                                mismatch: false
                            });
                        }
                    }
                } else {
                    // Album non trovato nella ricerca iniziale - cerca l'artista Gemini su Spotify
                    const geminiAlbumName = album.album || '';
                    const geminiArtistName = album.artist || '';
                    
                    debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → Album non trovato → Cercando artista "${geminiArtistName}" su Spotify...`);
                    
                    try {
                        // Cerca l'artista Gemini su Spotify
                        const { searchSpotifyArtist } = await import('../../services/spotify');
                        const spotifyArtistGemini = await searchSpotifyArtist(
                            geminiArtistName,
                            null,
                            album.genre || '',
                            null
                        ).catch(() => null);
                        
                        if (spotifyArtistGemini && spotifyArtistGemini.id) {
                            // Artista trovato! Ottieni i suoi album
                            const artistAlbumsResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}/albums`, {
                                params: {
                                    include_groups: 'album',
                                    limit: 10,
                                    market: 'IT'
                                }
                            }).catch(() => null);
                            
                            if (artistAlbumsResponse?.items?.length > 0) {
                                const validArtistAlbums = artistAlbumsResponse.items.filter(a => 
                                    a.album_type?.toLowerCase() === 'album'
                                );
                                
                                if (validArtistAlbums.length > 0) {
                                    const bestAlbum = validArtistAlbums[0];
                                    
                                    // Usa l'album dell'artista trovato
                                    setSpotifyData({
                                        ...bestAlbum,
                                        external_urls: bestAlbum.external_urls,
                                        artists: bestAlbum.artists
                                    });
                                    setIsSingle(false); // È un album
                                    
                                    // Cerca anche i dati dell'artista per l'immagine
                                    const artistResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}`).catch(() => null);
                                    if (artistResponse) {
                                        setArtistData(artistResponse);
                                    }
                                    
                                    debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ✅ Artista trovato → Album selezionato: "${bestAlbum.name}"`);
                                    
                                    geminiLogger.logGeminiSpotifyComparison({
                                        query: query || 'unknown',
                                        searchType: searchType || 'album',
                                        position: position || 0,
                                        geminiAlbum: geminiAlbumName,
                                        geminiArtist: geminiArtistName,
                                        geminiMatch: album.match || '0.8',
                                        spotifyFound: true,
                                        spotifyAlbum: bestAlbum.name,
                                        spotifyArtist: spotifyArtistGemini.name,
                                        mismatch: false,
                                        fixedWithArtistAlbum: true
                                    });
                                } else {
                                    // Nessun album valido - cerca singoli
                                    const validSingles = artistAlbumsResponse.items.filter(a => 
                                        a.album_type?.toLowerCase() === 'single'
                                    );
                                    
                                    if (validSingles.length > 0) {
                                        const bestSingle = validSingles[0];
                                        
                                        // Usa il singolo più famoso
                                        setSpotifyData({
                                            ...bestSingle,
                                            external_urls: bestSingle.external_urls,
                                            artists: bestSingle.artists
                                        });
                                        setIsSingle(true); // È un singolo
                                        
                                        // Cerca anche i dati dell'artista per l'immagine
                                        const artistResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}`).catch(() => null);
                                        if (artistResponse) {
                                            setArtistData(artistResponse);
                                        }
                                        
                                        debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ✅ Artista trovato → Nessun album, selezionato singolo: "${bestSingle.name}"`);
                                        
                                        geminiLogger.logGeminiSpotifyComparison({
                                            query: query || 'unknown',
                                            searchType: searchType || 'album',
                                            position: position || 0,
                                            geminiAlbum: geminiAlbumName,
                                            geminiArtist: geminiArtistName,
                                            geminiMatch: album.match || '0.8',
                                            spotifyFound: true,
                                            spotifyAlbum: bestSingle.name,
                                            spotifyArtist: spotifyArtistGemini.name,
                                            mismatch: false,
                                            fixedWithArtistAlbum: true,
                                            isSingle: true
                                        });
                                    } else {
                                        // Artista trovato ma nessun album valido
                                        debugWarn(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ⚠️ Artista trovato ma nessun album valido`);
                                        geminiLogger.logGeminiSpotifyComparison({
                                            query: query || 'unknown',
                                            searchType: searchType || 'album',
                                            position: position || 0,
                                            geminiAlbum: geminiAlbumName,
                                            geminiArtist: geminiArtistName,
                                            geminiMatch: album.match || '0.8',
                                            spotifyFound: false,
                                            mismatch: false
                                        });
                                    }
                                }
                            } else {
                                // Nessun album - cerca singoli
                                const singlesResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}/albums`, {
                                    params: {
                                        include_groups: 'single',
                                        limit: 10,
                                        market: 'IT'
                                    }
                                }).catch(() => null);
                                
                                if (singlesResponse?.items?.length > 0) {
                                    const bestSingle = singlesResponse.items[0];
                                    
                                    // Usa il singolo più famoso
                                    setSpotifyData({
                                        ...bestSingle,
                                        external_urls: bestSingle.external_urls,
                                        artists: bestSingle.artists
                                    });
                                    setIsSingle(true); // È un singolo
                                    
                                    // Cerca anche i dati dell'artista per l'immagine
                                    const artistResponse = await SpotifyService.request(`/artists/${spotifyArtistGemini.id}`).catch(() => null);
                                    if (artistResponse) {
                                        setArtistData(artistResponse);
                                    }
                                    
                                    debugLog(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ✅ Artista trovato → Nessun album, selezionato singolo: "${bestSingle.name}"`);
                                    
                                    geminiLogger.logGeminiSpotifyComparison({
                                        query: query || 'unknown',
                                        searchType: searchType || 'album',
                                        position: position || 0,
                                        geminiAlbum: geminiAlbumName,
                                        geminiArtist: geminiArtistName,
                                        geminiMatch: album.match || '0.8',
                                        spotifyFound: true,
                                        spotifyAlbum: bestSingle.name,
                                        spotifyArtist: spotifyArtistGemini.name,
                                        mismatch: false,
                                        fixedWithArtistAlbum: true,
                                        isSingle: true
                                    });
                                } else {
                                    // Artista trovato ma nessun album né singoli
                                    debugWarn(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ⚠️ Artista trovato ma nessun album né singoli`);
                                    geminiLogger.logGeminiSpotifyComparison({
                                        query: query || 'unknown',
                                        searchType: searchType || 'album',
                                        position: position || 0,
                                        geminiAlbum: geminiAlbumName,
                                        geminiArtist: geminiArtistName,
                                        geminiMatch: album.match || '0.8',
                                        spotifyFound: false,
                                        mismatch: false
                                    });
                                }
                            }
                        } else {
                            // Artista NON trovato su Spotify - non inventare nulla
                            debugWarn(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ❌ Artista non trovato su Spotify`);
                            geminiLogger.logGeminiSpotifyComparison({
                                query: query || 'unknown',
                                searchType: searchType || 'album',
                                position: position || 0,
                                geminiAlbum: geminiAlbumName,
                                geminiArtist: geminiArtistName,
                                geminiMatch: album.match || '0.8',
                                spotifyFound: false,
                                mismatch: false
                            });
                        }
                    } catch (error) {
                        debugWarn(`[${position + 1}] Gemini: "${geminiAlbumName}" by "${geminiArtistName}" → ❌ Errore ricerca artista: ${error.message}`);
                        geminiLogger.logGeminiSpotifyComparison({
                            query: query || 'unknown',
                            searchType: searchType || 'album',
                            position: position || 0,
                            geminiAlbum: geminiAlbumName,
                            geminiArtist: geminiArtistName,
                            geminiMatch: album.match || '0.8',
                            spotifyFound: false,
                            mismatch: false
                        });
                    }
                }
                
                setHasLoaded(true);
            } catch (error) {
                console.error('Error loading album data:', error);
                setHasLoaded(true);
            }
        };

        fetchData();
    }, [album.album, album.artist, shouldLoad, hasLoaded]);

    const displayData = spotifyData || album;
    
    // Immagine album per avatar
    const albumImageUrl = spotifyData?.images?.[0]?.url || null;
    const artistImageUrl = artistData?.images?.[0]?.url || null;
    const artistSpotifyId = spotifyData?.artists?.[0]?.id || null;
    const artistSpotifyUrl = artistData?.external_urls?.spotify || (artistSpotifyId ? `https://open.spotify.com/artist/${artistSpotifyId}` : null);

    useEffect(() => {
        if (!albumImageUrl) {
            setBackground('#121212');
            return;
        }

        const cachedGradient = slideGradientCache.get(albumImageUrl);
        if (cachedGradient) {
            setBackground(cachedGradient);
            return;
        }

        let cancelled = false;
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = albumImageUrl;

        img.onload = () => {
            if (cancelled) return;
            try {
                const colorThief = new ColorThief();
                const [r, g, b] = colorThief.getColor(img);
                const gradient = `linear-gradient(180deg, rgb(${r},${g},${b}) 0%, #121212 100%)`;
                slideGradientCache.set(albumImageUrl, gradient);
                setBackground(gradient);
            } catch (error) {
                setBackground('#121212');
            }
        };

        img.onerror = () => {
            if (cancelled) return;
            setBackground('#121212');
        };

        return () => {
            cancelled = true;
        };
    }, [albumImageUrl]);

    return (
        <div className="artist-slide">
            <div 
                className="artist-slide-background"
                style={{ 
                    background: background,
                    opacity: isActive ? 1 : 0
                }}
            />
            <div className="artist-slide-overlay" />

            <div className="artist-content">
                {/* Immagine principale (album) */}
                <div className="relative">
                    <div className="artist-image-container">
                        {albumImageUrl ? (
                            <img 
                                src={albumImageUrl}
                                alt={album.album}
                                className="artist-image"
                                onError={() => {
                                    setBackground('#121212');
                                }}
                            />
                        ) : (
                            <div 
                                className="artist-image-placeholder"
                                style={{
                                    backgroundColor: '#000000',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '8px'
                                }}
                            />
                        )}
                    </div>
                </div>

                <h2 className="artist-name">{spotifyData?.name || album.album || 'Unknown Album'}</h2>
                
                {/* Generi */}
                {album.genre && (
                    <p className="artist-genres">
                        {album.genre}
                    </p>
                )}
                
                {/* Data di uscita */}
                {spotifyData?.release_date && (
                    <p className="artist-genres" style={{ 
                        marginTop: '4px', 
                        fontSize: '14px', 
                        opacity: 0.8 
                    }}>
                        {isSingle ? 'Singolo' : 'Album'} {new Date(spotifyData.release_date).toLocaleDateString('it-IT', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}
                    </p>
                )}

                {/* Artista - avatar molto più piccolo, nome più piccolo, centrati */}
                <div style={{
                    marginTop: '25px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    maxWidth: '90%',
                    margin: '25px auto 0'
                }}>
                    {/* Foto artista - ancora più piccola */}
                    <a
                        href={artistSpotifyUrl || `https://open.spotify.com/search/${encodeURIComponent(album.artist)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'block',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                            flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        {artistImageUrl ? (
                            <img 
                                src={artistImageUrl}
                                alt={album.artist}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: '#2a2a2a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                            }}>
                                <i className="bi bi-person" style={{ fontSize: '14px', color: '#666' }} />
                            </div>
                        )}
                    </a>
                    
                    {/* Nome artista - più piccolo, sempre cliccabile, centrato */}
                    <a
                        href={artistSpotifyUrl || `https://open.spotify.com/search/${encodeURIComponent(album.artist)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '500',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            alignSelf: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#1db954';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'white';
                        }}
                    >
                        {spotifyData?.artists?.[0]?.name || album.artist}
                    </a>
                </div>

                {/* Link a Spotify (all'album) e LastFM (all'artista) */}
                <div className="external-links" style={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    gap: '8px', 
                    justifyContent: 'center',
                    marginTop: '1.5rem',
                    flexWrap: 'wrap'
                }}>
                    {/* Pulsante Spotify - link all'album */}
                    <a
                        href={spotifyData?.external_urls?.spotify || `https://open.spotify.com/search/${encodeURIComponent(album.album + ' ' + album.artist)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="spotify-button"
                        style={{
                            backgroundColor: '#1db954',
                            color: 'white',
                            flex: '1',
                            maxWidth: '140px',
                            minWidth: '120px',
                            fontSize: '14px',
                            padding: '12px 8px',
                            textAlign: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="bi bi-spotify" style={{ marginRight: '4px' }} />
                        {spotifyData?.external_urls?.spotify ? 'Spotify' : 'Search'}
                    </a>
                    
                    {/* Pulsante LastFM - link all'artista */}
                    <a
                        href={album.url || `https://www.last.fm/music/${encodeURIComponent(album.artist)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="spotify-button"
                        style={{
                            backgroundColor: '#d51007',
                            color: 'white',
                            flex: '1',
                            maxWidth: '140px',
                            minWidth: '120px',
                            fontSize: '14px',
                            padding: '12px 8px',
                            textAlign: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <img src={`${process.env.PUBLIC_URL}/img/lastfm_logo.svg`} alt="Last.fm" width="16" height="16" style={{ marginRight: '4px' }} />
                        LastFM
                    </a>
                </div>
            </div>
        </div>
    );
};

const TrackSlide = ({ track, isActive, shouldLoad, pageType, position, query, searchType }) => {
    const [background, setBackground] = useState('#121212');
    const [spotifyData, setSpotifyData] = useState(null);
    const [artistData, setArtistData] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);

    // Lazy loading: carica solo quando shouldLoad è true
    useEffect(() => {
        if (!shouldLoad || hasLoaded) return;

        const fetchData = async () => {
            try {
                debugLog(`🔄 Loading Spotify data for track: ${track.track} by ${track.artist}`);
                
                // 1. Cerca la traccia su Spotify
                const trackResponse = await SpotifyService.request('/search', {
                    params: {
                        q: `track:"${track.track}" artist:"${track.artist}"`,
                        type: 'track',
                        limit: 1
                    }
                }).catch(e => {
                    debugWarn(`Spotify track search failed:`, e.message);
                    return null;
                });

                const spotifyTrack = trackResponse?.tracks?.items?.[0];
                if (spotifyTrack) {
                    setSpotifyData({
                        ...spotifyTrack,
                        external_urls: spotifyTrack.external_urls,
                        album: spotifyTrack.album,
                        artists: spotifyTrack.artists
                    });

                    // 2. Cerca l'artista per avere l'immagine dell'artista
                    if (spotifyTrack.artists?.[0]?.id) {
                        const artistResponse = await SpotifyService.request(`/artists/${spotifyTrack.artists[0].id}`).catch(() => null);
                        if (artistResponse) {
                            setArtistData(artistResponse);
                        }
                    }
                    
                    // Log confronto Gemini vs Spotify
                    const spotifyTrackName = spotifyTrack.name || '';
                    const spotifyArtistName = spotifyTrack.artists?.[0]?.name || '';
                    const geminiTrackName = track.track || '';
                    const geminiArtistName = track.artist || '';
                    const isMatch = spotifyTrackName.toLowerCase().trim() === geminiTrackName.toLowerCase().trim() &&
                                   spotifyArtistName.toLowerCase().trim() === geminiArtistName.toLowerCase().trim();
                    
                    geminiLogger.logGeminiSpotifyComparison({
                        query: query || 'unknown',
                        searchType: searchType || 'track',
                        position: position || 0,
                        geminiTrack: geminiTrackName,
                        geminiArtist: geminiArtistName,
                        geminiMatch: track.match || '0.8',
                        spotifyFound: true,
                        spotifyTrack: spotifyTrackName,
                        spotifyArtist: spotifyArtistName,
                        mismatch: !isMatch
                    });
                } else {
                    // Log invenzione di Gemini
                    geminiLogger.logGeminiSpotifyComparison({
                        query: query || 'unknown',
                        searchType: searchType || 'track',
                        position: position || 0,
                        geminiTrack: track.track || '',
                        geminiArtist: track.artist || '',
                        geminiMatch: track.match || '0.8',
                        spotifyFound: false,
                        mismatch: false
                    });
                }
                
                setHasLoaded(true);
            } catch (error) {
                console.error('Error loading track data:', error);
                setHasLoaded(true);
            }
        };

        fetchData();
    }, [track.track, track.artist, shouldLoad, hasLoaded]);

    const displayData = spotifyData || track;
    
    // Immagine album per avatar e per sezione album
    const albumImageUrl = spotifyData?.album?.images?.[0]?.url || null;
    const artistImageUrl = artistData?.images?.[0]?.url || null;
    const artistSpotifyId = spotifyData?.artists?.[0]?.id || null;
    const artistSpotifyUrl = artistData?.external_urls?.spotify || (artistSpotifyId ? `https://open.spotify.com/artist/${artistSpotifyId}` : null);

    useEffect(() => {
        if (!albumImageUrl) {
            setBackground('#121212');
            return;
        }

        const cachedGradient = slideGradientCache.get(albumImageUrl);
        if (cachedGradient) {
            setBackground(cachedGradient);
            return;
        }

        let cancelled = false;
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = albumImageUrl;

        img.onload = () => {
            if (cancelled) return;
            try {
                const colorThief = new ColorThief();
                const [r, g, b] = colorThief.getColor(img);
                const gradient = `linear-gradient(180deg, rgb(${r},${g},${b}) 0%, #121212 100%)`;
                slideGradientCache.set(albumImageUrl, gradient);
                setBackground(gradient);
            } catch (error) {
                setBackground('#121212');
            }
        };

        img.onerror = () => {
            if (cancelled) return;
            setBackground('#121212');
        };

        return () => {
            cancelled = true;
        };
    }, [albumImageUrl]);

    return (
        <div className="artist-slide">
            <div 
                className="artist-slide-background"
                style={{ 
                    background: background,
                    opacity: isActive ? 1 : 0
                }}
            />
            <div className="artist-slide-overlay" />

            <div className="artist-content">
                {/* Immagine principale (album) - come per artisti */}
                <div className="relative">
                    <div className="artist-image-container">
                        {albumImageUrl ? (
                            <img 
                                src={albumImageUrl}
                                alt={track.track}
                                className="artist-image"
                                onError={() => {
                                    setBackground('#121212');
                                }}
                            />
                        ) : (
                            <div 
                                className="artist-image-placeholder"
                                style={{
                                    backgroundColor: '#000000',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '8px'
                                }}
                            />
                        )}
                    </div>
                </div>

                <h2 className="artist-name">{track.track || 'Unknown Track'}</h2>
                
                {/* Generi */}
                {track.genre && (
                    <p className="artist-genres">
                        {track.genre}
                    </p>
                )}
                

                {/* Artista - foto tonda a sinistra e nome più grande vicino, entrambi cliccabili */}
                <div style={{
                    marginTop: '15px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: '12px',
                    maxWidth: '90%',
                    margin: '15px auto 0',
                    paddingLeft: '5%'
                }}>
                    {/* Foto artista - tonda, stile semplice */}
                    <a
                        href={artistSpotifyUrl || `https://open.spotify.com/search/${encodeURIComponent(track.artist)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'block',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                            flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        {artistImageUrl ? (
                            <img 
                                src={artistImageUrl}
                                alt={track.artist}
                                style={{
                                    width: '70px',
                                    height: '70px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '70px',
                                height: '70px',
                                borderRadius: '50%',
                                backgroundColor: '#2a2a2a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                            }}>
                                <i className="bi bi-person" style={{ fontSize: '32px', color: '#666' }} />
                            </div>
                        )}
                    </a>
                    
                    {/* Nome artista - più grande, cliccabile, più vicino all'immagine */}
                    <a
                        href={artistSpotifyUrl || `https://open.spotify.com/search/${encodeURIComponent(track.artist)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: 'white',
                            fontSize: '20px',
                            fontWeight: '600',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            alignSelf: 'normal',
                            paddingTop: '8px',
                            textAlign: 'left'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#1db954';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'white';
                        }}
                    >
                        {track.artist}
                    </a>
                </div>

                {/* Link a Spotify (al brano) e LastFM (all'artista) */}
                <div className="external-links" style={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    gap: '8px', 
                    justifyContent: 'center',
                    marginTop: '1.5rem',
                    flexWrap: 'wrap'
                }}>
                    {/* Pulsante Spotify - link al brano */}
                    <a
                        href={spotifyData?.external_urls?.spotify || `https://open.spotify.com/search/${encodeURIComponent(track.track + ' ' + track.artist)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="spotify-button"
                        style={{
                            backgroundColor: '#1db954',
                            color: 'white',
                            flex: '1',
                            maxWidth: '140px',
                            minWidth: '120px',
                            fontSize: '14px',
                            padding: '12px 8px',
                            textAlign: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="bi bi-spotify" style={{ marginRight: '4px' }} />
                        {spotifyData?.external_urls?.spotify ? 'Spotify' : 'Search'}
                    </a>
                    
                    {/* Pulsante LastFM - link all'artista */}
                    <a
                        href={track.url || `https://www.last.fm/music/${encodeURIComponent(track.artist)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="spotify-button"
                        style={{
                            backgroundColor: '#d51007',
                            color: 'white',
                            flex: '1',
                            maxWidth: '140px',
                            minWidth: '120px',
                            fontSize: '14px',
                            padding: '12px 8px',
                            textAlign: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <img src={`${process.env.PUBLIC_URL}/img/lastfm_logo.svg`} alt="Last.fm" width="16" height="16" style={{ marginRight: '4px' }} />
                        LastFM
                    </a>
                </div>
            </div>
        </div>
    );
};

const ActionSlide = ({ onHome, onDiscover, onToggleChart, discoverButtonText, discoverIcon, showDiscoverButton = true, t, pageType, onBackToDiscovery, onBackToSearch, onNewSearch, language }) => (
    <div className="action-slide">
        <div className="action-buttons">
            {/* Similar: sola Nuova ricerca (reset pagina base) */}
            {pageType === 'similar' && (
                <button
                    onClick={onNewSearch || onBackToSearch}
                    className="discover-button"
                >
                    <i className="bi bi-search" />
                    {language === 'it' ? 'Nuova ricerca' : 'New search'}
                </button>
            )}
            
            {/* Daily discovery: mostra "Torna all'approfondimento" e "Home" */}
            {pageType === 'daily-discovery-artists' && (
                <>
                    <button
                        onClick={onBackToDiscovery}
                        className="discover-button"
                        style={{ marginBottom: '15px' }}
                    >
                        {t('showResult.backToDiscovery')}
                    </button>
                    <button
                        onClick={onHome}
                        className="home-button"
                    >
                        <i className="bi bi-house" />
                        {t('showResult.backToHome')}
                    </button>
                </>
            )}
            
            {/* Random: solo "Scopri altro" */}
            {pageType === 'random' && showDiscoverButton && (
                <button
                    onClick={onDiscover}
                    className="discover-button"
                >
                    <i className={discoverIcon || "bi bi-arrow-repeat"} />
                    {discoverButtonText || t('showResult.discoverMore')}
                </button>
            )}
            
            {/* Top-artists: mostra toggle chart + home */}
            {pageType === 'top-artists' && (
                <>
                    {showDiscoverButton && (
                        <button
                            onClick={onToggleChart || onDiscover}
                            className="discover-button"
                            style={{ marginBottom: '15px' }}
                        >
                            <i className={discoverIcon || "bi bi-arrow-repeat"} />
                            {discoverButtonText || t('showResult.discoverMore')}
                        </button>
                    )}
                    <button
                        onClick={onHome}
                        className="home-button"
                    >
                        <i className="bi bi-house" />
                        {t('showResult.backToHome')}
                    </button>
                </>
            )}

            {/* Search: mostra "Cerca altro" + home */}
            {pageType === 'search' && (
                <>
                    <button
                        onClick={onBackToSearch}
                        className="discover-button"
                        style={{ marginBottom: '15px' }}
                    >
                        <i className="bi bi-search" />
                        {t('showResult.searchAnother')}
                    </button>
                    <button
                        onClick={onHome}
                        className="home-button"
                    >
                        <i className="bi bi-house" />
                        {t('showResult.backToHome')}
                    </button>
                </>
            )}
        </div>
    </div>
);

const ShowResult = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { language } = useLanguage();
    const { t } = useTranslation(language);
    const { isAuthenticated, refreshFavorites } = useApp();
    
    // Determina il tipo corrente (da state o pathname)
    const currentPath = window.location.pathname;
    const derivedType = state?.type || (currentPath === '/random' ? 'random' : currentPath === '/similar' ? 'similar' : null);
    const [currentType, setCurrentType] = useState(derivedType);

    // LOGICA DI INIZIALIZZAZIONE SINCRONA
    // Calcola lo stato iniziale leggendo persistence/state SUBITO, per evitare render a vuoto
    const [initialData] = useState(() => {
        console.log(`[ShowResult] 🚀 MOUNTING NEW COMPONENT INSTANCE. Path: ${window.location.pathname}`);
        const result = {
            items: [],
            filteredItems: [],
            activeIndex: 0,
            query: '',
            searchType: 'artist',
            hasRandomPersistence: false,
            hasSearchPersistence: false,
            hasDailyPersistence: false,
            freeTextAnalysis: null
        };

        console.log(`[ShowResult] 🏗️ Initializing state synchronously for type: ${derivedType}`);

        if (!derivedType) return result;

        // 1. Daily/Top: usano state.items (da navigazione o da persistenza via Header)
        if (derivedType === 'daily-discovery-artists' || derivedType === 'top-artists') {
            const itemsSource = state?.items?.length ? state.items : null;
            if (itemsSource?.length) {
                result.items = itemsSource;
                result.filteredItems = itemsSource;
                const savedDaily = PersistenceService.getDailyState();
                const savedIndex = state?.savedIndex ?? savedDaily?.activeIndex;
                if (savedIndex != null && savedIndex > 0) {
                    result.activeIndex = savedIndex;
                    result.hasDailyPersistence = true;
                } else {
                    result.activeIndex = 1;
                    result.hasDailyPersistence = false;
                }
            }
            return result;
        }

        // 2. Random: prova persistenza se forceReset è true o items vuoti
        if (derivedType === 'random') {
            // Se c'è forceReset, ignoriamo state.items (che comunque sarebbe vuoto per random) e usiamo persistenza
            // Oppure se state.items è vuoto
            const saved = PersistenceService.getRandomState();
            if (saved?.items?.length) {
                console.log('[ShowResult] 📦 Hydrated Random synchronously:', saved.items.length);
                result.items = saved.items;
                result.filteredItems = saved.items;
                result.activeIndex = saved.activeIndex || 0;
                result.hasRandomPersistence = true;
            } else {
                console.log('[ShowResult] ⚠️ Random: no persistence found, starting empty');
            }
            return result;
        }

        // 3. Similar: usa state.items se presenti (e non forceReset), altrimenti persistenza
        if (derivedType === 'similar') {
            // Se arriviamo da una ricerca fresca (state.items popolato) e NON è un forceReset
            // NOTA: Se c'è forceReset, significa NUOVA RICERCA, quindi dobbiamo ignorare sia state.items vecchi che la persistenza
            if (state?.items?.length && !state.forceReset) {
                console.log('[ShowResult] 📥 Similar: using state.items:', state.items.length);
                result.items = state.items;
                result.filteredItems = []; 
                result.activeIndex = state.savedIndex || 0;
                result.query = state.query || '';
                result.searchType = state.searchType || 'artist';
                result.freeTextAnalysis = state.freeTextAnalysis || null;
            } else {
                // Tentativo persistenza SOLO se non è un forceReset (nuova ricerca)
                // Se è forceReset, vogliamo partire puliti
                const shouldCheckPersistence = !state?.forceReset;
                const saved = shouldCheckPersistence ? PersistenceService.getSearchState() : null;
                
                if (saved?.items?.length) {
                    console.log('[ShowResult] 📦 Hydrated Similar synchronously:', saved.items.length);
                    result.items = saved.items;
                    result.filteredItems = saved.items; // Persistenza ha già items filtrati
                    result.activeIndex = saved.activeIndex || 0;
                    result.query = saved.query || '';
                    result.searchType = saved.searchType || 'artist';
                    result.freeTextAnalysis = saved.freeTextAnalysis || null;
                    result.hasSearchPersistence = true;
                } else {
                    // Fallback o Nuova Ricerca
                    console.log('[ShowResult] 🆕 New Search or No Persistence');
                    // Se è una nuova ricerca, result rimane vuoto e i useEffect faranno partire il bridge
                }
            }
            return result;
        }

        return result;
    });
    
    const [items, setItems] = useState(initialData.items);
    const [filteredItems, setFilteredItems] = useState(initialData.filteredItems);
    // SE non abbiamo items iniziali, siamo in caricamento (bridge/spinner)
    const [loading, setLoading] = useState(initialData.items.length === 0);
    const [error, setError] = useState(null);
    const [isFilteringArtists, setIsFilteringArtists] = useState(false); // Flag per tracciare il filtraggio
    // Per random, non usare mai savedIndex - sempre inizia da 0 (Hero)
    // Se è una nuova ricerca (no persistenza), activeIndex deve essere 0 o 1 a seconda della UX desiderata
    // Se è similar e abbiamo items (persistenza), usiamo savedIndex
    const [activeIndex, setActiveIndex] = useState(initialData.activeIndex);
    const [searchQuery, setSearchQuery] = useState(initialData.query);
    const [currentSearchQuery, setCurrentSearchQuery] = useState(initialData.query); // Per il loading screen
    const [currentArtistImage, setCurrentArtistImage] = useState(null); // Per il loading screen
    const [searchMessage, setSearchMessage] = useState(''); // Per messaggi sotto l'input
    const [currentCountry, setCurrentCountry] = useState(null);
    const [swiperInstance, setSwiperInstance] = useState(null);
    const [swiperReady, setSwiperReady] = useState(false); // Flag per indicare quando Swiper è inizializzato
    const [listenModal, setListenModal] = useState({ isOpen: false, artistName: null, albumName: null, ids: {}, mode: 'listen' });
    const [showArtistInfoModal, setShowArtistInfoModal] = useState(false);
    const [currentArtistInfo, setCurrentArtistInfo] = useState(null); // Info artista dall'AI
    const [searchType, setSearchType] = useState('artist'); // Sempre artisti
    const [isSearching, setIsSearching] = useState(false); // Flag per disabilitare pulsanti durante ricerca
    const [freeTextAnalysis, setFreeTextAnalysis] = useState(initialData.freeTextAnalysis);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false); // Modal per mostrare l'analisi
    const [currentPrompt, setCurrentPrompt] = useState(''); // Prompt corrente per il loading screen
    const [isVoiceTranscript, setIsVoiceTranscript] = useState(false); // Flag per indicare se è trascrizione vocale
    const [bridgeMessage, setBridgeMessage] = useState(''); // Messaggio bridge di Gemini 2.0
    const [heroSuggestionSeed, setHeroSuggestionSeed] = useState(() => Date.now());
    const [hasRandomPersistence, setHasRandomPersistence] = useState(initialData.hasRandomPersistence);
    const [hasSearchPersistence, setHasSearchPersistence] = useState(initialData.hasSearchPersistence);
    const [hasDailyPersistence, setHasDailyPersistence] = useState(initialData.hasDailyPersistence);
    const [isGeneratingRandom, setIsGeneratingRandom] = useState(false);
    const [isDesktopLayout, setIsDesktopLayout] = useState(
        typeof window !== 'undefined' ? window.innerWidth >= 769 : false
    );
    const requestMade = useRef(false);
    const previousTypeRef = useRef(currentType);
    const searchHydrationAttemptedRef = useRef(false);
    const dailyHydrationAttemptedRef = useRef(false);
    const isNavigatingBackToDailyRef = useRef(false); // Evita che il cleanup re-salvi quando torniamo indietro
    const forceResetProcessedRef = useRef(false); // Evita che forceReset venga processato più volte
    const searchWrapperRef = useRef(null); // Ref per il container input + pulsanti
    const bridgeRequestIdRef = useRef(0);
    const firstSlideReadyRef = useRef(false);
    const [firstSlideReady, setFirstSlideReady] = useState(false);
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const isRandomExperience = currentType === 'random' || pathname === '/random';
    const isSearchExperience = currentType === 'similar' || pathname === '/similar';
    const isDailyExperience = currentType === 'daily-discovery-artists' || pathname === '/daily-discovery-artists';

    useEffect(() => {
        const onResize = () => setIsDesktopLayout(window.innerWidth >= 769);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const typeChangedThisRender = previousTypeRef.current !== currentType;
    const randomHydrationAttemptedRef = useRef(false);
    const pendingRandomAutoSlideRef = useRef(false);
    const isNewRandomRequestRef = useRef(false); // Flag per tracciare nuove richieste Random
    const isNewSimilarRequestRef = useRef(false); // Flag per tracciare nuove richieste Similar
    const similarSlideCompletedRef = useRef(true); // Flag per tracciare se lo slide a 1 è completato per similar
    const randomAutoStartedRef = useRef(false); // Auto-avvio random alla prima apertura vuota

    useEffect(() => {
        previousTypeRef.current = currentType;
    }, [currentType]);

    useEffect(() => {
        if (!typeChangedThisRender || !swiperInstance) {
            return;
        }
        if (swiperInstance.destroyed) {
            return;
        }
        try {
            console.log('[Swiper Transition] 🔧 Destroying previous Swiper instance before remount');
            swiperInstance.destroy(true, false);
        } catch (error) {
            console.warn('[Swiper Transition] ⚠️ Swiper destroy error:', error);
        } finally {
            setSwiperInstance(null);
            setSwiperReady(false);
        }
    }, [typeChangedThisRender, swiperInstance]);

    const hydrateRandomFromPersistence = useCallback(() => {
        console.log('[Random Persistence] 🔍 Attempting hydration...');
        const savedRandom = PersistenceService.getRandomState();
        console.log('[Random Persistence] 📦 Saved state:', savedRandom);
        
        if (savedRandom?.items?.length) {
            console.log('[Random Persistence] ✅ Found persisted data:', {
                itemsCount: savedRandom.items.length,
                savedIndex: savedRandom.activeIndex
            });
            setItems(savedRandom.items);
            setFilteredItems(savedRandom.items);
            const heroOffset = 1;
            const actionSlideIndex = heroOffset + savedRandom.items.length;
            const rawIndex = typeof savedRandom.activeIndex === 'number'
                ? savedRandom.activeIndex
                : 0;
            const nextIndex = Math.min(Math.max(0, rawIndex), actionSlideIndex);
            console.log('[Random Persistence] 📍 Setting activeIndex to:', nextIndex);
            setActiveIndex(nextIndex);
            setHasRandomPersistence(true);
            setLoading(false);
            setIsFilteringArtists(false); // Assicura che il filtering non riparta
            setError(null); // Pulisci eventuali errori
            requestMade.current = true;
            console.log('[Random Persistence] ✅ Hydration completed, state:', {
                itemsLength: savedRandom.items.length,
                filteredItemsLength: savedRandom.items.length,
                activeIndex: nextIndex,
                loading: false,
                isFilteringArtists: false
            });
            // Marca hydration completata DOPO aver impostato tutto
            setTimeout(() => {
                randomHydrationAttemptedRef.current = false;
            }, 500);
            return true;
        }

        console.log('[Random Persistence] ❌ No persisted data found');
        setHasRandomPersistence(false);
        setItems([]);
        setFilteredItems([]);
        setActiveIndex(0);
        setLoading(false);
        requestMade.current = false;
        randomHydrationAttemptedRef.current = false;
        return false;
    }, []);

    const hydrateSearchFromPersistence = useCallback(() => {
        console.log('[Search Persistence] 🔍 Attempting hydration...');
        const savedSearch = PersistenceService.getSearchState();
        console.log('[Search Persistence] 📦 Saved state:', savedSearch);
        
        if (savedSearch?.items?.length && savedSearch?.query) {
            console.log('[Search Persistence] ✅ Found persisted data:', {
                itemsCount: savedSearch.items.length,
                savedIndex: savedSearch.activeIndex,
                query: savedSearch.query,
                searchType: savedSearch.searchType
            });
            // IMPORTANTE: setta hasSearchPersistence PRIMA di items/filteredItems per evitare problemi di timing
            setHasSearchPersistence(true);
            setItems(savedSearch.items);
            // IMPORTANTE: setta filteredItems PRIMA di items per evitare che il filtering riparta
            setFilteredItems(savedSearch.items);
            // Input resta vuoto: il prompt resta solo in currentSearchQuery (persistenza / bridge)
            setSearchQuery('');
            setCurrentSearchQuery(savedSearch.query || '');
            setSearchType(savedSearch.searchType || 'artist');
            if (savedSearch.freeTextAnalysis) {
                setFreeTextAnalysis(savedSearch.freeTextAnalysis);
            }
            const rawIndex = typeof savedSearch.activeIndex === 'number'
                ? savedSearch.activeIndex
                : 0;
            const heroOffset = 1;
            const actionSlideIndex = heroOffset + savedSearch.items.length;
            const nextIndex = Math.min(Math.max(0, rawIndex), actionSlideIndex);
            console.log('[Search Persistence] 📍 Setting activeIndex to:', nextIndex);
            setActiveIndex(nextIndex);
            setLoading(false);
            setIsFilteringArtists(false); // Assicura che il filtering non riparta
            requestMade.current = true;
            // IMPORTANTE: Se Swiper è già inizializzato, forzalo alla posizione corretta
            if (swiperInstance && swiperInstance.initialized && !swiperInstance.destroyed) {
                console.log('[Search Persistence] 🎯 Swiper already initialized, forcing slide to:', nextIndex);
                setTimeout(() => {
                    if (swiperInstance && swiperInstance.initialized && !swiperInstance.destroyed) {
                        swiperInstance.slideTo(nextIndex, 0);
                    }
                }, 100);
            }
            // Marca hydration completata DOPO aver impostato tutto
            setTimeout(() => {
                searchHydrationAttemptedRef.current = false;
            }, 500);
            return true;
        }

        console.log('[Search Persistence] ❌ No persisted data found');
        setHasSearchPersistence(false);
        setItems([]);
        setFilteredItems([]);
        setActiveIndex(0);
        setLoading(false);
        requestMade.current = false;
        searchHydrationAttemptedRef.current = false;
        return false;
    }, []);

    const hydrateDailyFromPersistence = useCallback((currentItems) => {
        console.log('[Daily Persistence] 🔍 Attempting hydration...');
        const savedDaily = PersistenceService.getDailyState();
        console.log('[Daily Persistence] 📦 Saved state:', savedDaily);
        
        // Per Daily, salviamo solo la posizione (la lista viene dal DB)
        if (savedDaily?.activeIndex !== undefined && currentItems?.length > 0) {
            console.log('[Daily Persistence] ✅ Found persisted position:', {
                savedIndex: savedDaily.activeIndex,
                currentItemsCount: currentItems.length
            });
            const rawIndex = typeof savedDaily.activeIndex === 'number'
                ? savedDaily.activeIndex
                : 0;
            const nextIndex = Math.min(Math.max(0, rawIndex), currentItems.length);
            console.log('[Daily Persistence] 📍 Setting activeIndex to:', nextIndex);
            setActiveIndex(nextIndex);
            setHasDailyPersistence(true);
            // Marca hydration completata DOPO aver impostato tutto
            setTimeout(() => {
                dailyHydrationAttemptedRef.current = false;
            }, 500);
            return true;
        }

        console.log('[Daily Persistence] ❌ No persisted position found');
        setHasDailyPersistence(false);
        setActiveIndex(0);
        dailyHydrationAttemptedRef.current = false;
        return false;
    }, []);

    useEffect(() => {
        if (items.length === 0) {
            firstSlideReadyRef.current = false;
            setFirstSlideReady(false);
        }
    }, [items.length]);

    useEffect(() => {
        // Quando currentType diventa 'random', prova l'hydration
        // IMPORTANTE: controlla anche il pathname per gestire il caso in cui torni su /random
        if ((currentType === 'random' || pathname === '/random') && !randomHydrationAttemptedRef.current) {
            console.log('[Random Persistence] 🔄 currentType is random or pathname is /random, attempting hydration...');
            const hydrated = hydrateRandomFromPersistence();
            if (hydrated) {
                console.log('[Random Persistence] ✅ Hydration successful from useEffect');
            } else {
                console.log('[Random Persistence] ⚠️ No hydration data found in useEffect');
            }
        }
    }, [currentType, pathname, hydrateRandomFromPersistence]);

    useEffect(() => {
        // Quando currentType diventa 'similar', prova l'hydration
        if (currentType === 'similar' && !searchHydrationAttemptedRef.current) {
            console.log('[Search Persistence] 🔄 currentType is similar, attempting hydration...');
            const hydrated = hydrateSearchFromPersistence();
            if (hydrated) {
                console.log('[Search Persistence] ✅ Hydration successful from useEffect');
            } else {
                console.log('[Search Persistence] ⚠️ No hydration data found in useEffect');
            }
        }
    }, [currentType, hydrateSearchFromPersistence]);

    // Hydration per Daily quando gli items sono disponibili
    useEffect(() => {
        if ((currentType === 'daily-discovery-artists' || currentType === 'top-artists') && 
            filteredItems.length > 0 && 
            !dailyHydrationAttemptedRef.current) {
            console.log('[Daily Persistence] 🎯 Items loaded, attempting hydration...');
            dailyHydrationAttemptedRef.current = true;
            hydrateDailyFromPersistence(filteredItems);
        }
    }, [currentType, filteredItems.length, hydrateDailyFromPersistence]);

    useEffect(() => {
        if (currentType !== 'random') {
            randomHydrationAttemptedRef.current = false;
        }
        if (currentType !== 'similar') {
            searchHydrationAttemptedRef.current = false;
        }
        if (currentType !== 'daily-discovery-artists' && currentType !== 'top-artists') {
            dailyHydrationAttemptedRef.current = false;
        }
    }, [currentType]);

    // IMPORTANTE: Se Swiper si inizializza dopo l'hydration e activeIndex è diverso dalla posizione corrente,
    // forza lo slide alla posizione corretta (per mantenere la persistenza della posizione)
    useEffect(() => {
        if (currentType === 'similar' && hasSearchPersistence && swiperInstance && swiperInstance.initialized && !swiperInstance.destroyed) {
            const currentSwiperIndex = swiperInstance.activeIndex;
            if (activeIndex !== currentSwiperIndex && filteredItems.length > 0) {
                console.log('[Search Persistence] 🎯 Swiper initialized after hydration, forcing slide to saved position:', {
                    activeIndex,
                    currentSwiperIndex,
                    forcingTo: activeIndex
                });
                swiperInstance.slideTo(activeIndex, 0);
            }
        }
        // Per daily con persistenza: forza lo slide alla posizione salvata
        // (safeActiveIndex parte da 1 per i nuovi arrivi, ma la persistenza può avere un valore diverso)
        if (isDailyExperience && hasDailyPersistence && swiperInstance && swiperInstance.initialized && !swiperInstance.destroyed) {
            const currentSwiperIndex = swiperInstance.activeIndex;
            if (activeIndex !== currentSwiperIndex && filteredItems.length > 0) {
                console.log('[Daily Persistence] 🎯 Forcing slide to saved position:', activeIndex);
                swiperInstance.slideTo(activeIndex, 0);
            }
        }
    }, [swiperInstance, currentType, hasSearchPersistence, hasDailyPersistence, isDailyExperience, activeIndex, filteredItems.length]);

    // Salva posizione daily quando l'utente esce (click fuori, navigazione) — così al ritorno ripristiniamo
    useEffect(() => {
        return () => {
            if (isNavigatingBackToDailyRef.current) return; // Non salvare se stiamo tornando indietro intenzionalmente
            if ((currentType === 'daily-discovery-artists' || currentType === 'top-artists') && filteredItems.length > 0) {
                const toSave = { activeIndex };
                if (state?.discoveryId) toSave.discoveryId = state.discoveryId;
                if (state?.discoveryData) toSave.discoveryData = state.discoveryData;
                if (state?.query) toSave.query = state.query;
                if (state?.items?.length) toSave.items = state.items;
                console.log('[Daily Persistence] 💾 Saving on unmount:', activeIndex);
                PersistenceService.setDailyState(toSave);
            }
        };
    }, [currentType, activeIndex, filteredItems.length, state?.discoveryId, state?.discoveryData, state?.query, state?.items]);

    // RIMOSSO: salvataggio automatico di items e activeIndex
    // Ora il salvataggio avviene solo:
    // 1. items/filteredItems: quando cambiano (useEffect sotto)
    // 2. activeIndex: solo in onSlideChange dello Swiper (quando l'utente fa swipe)

    // RIMOSSO: useEffect per slideTo manuale. Usiamo initialSlide su Swiper per zero-latency.
    
    // useEffect(() => {
    //     const hasHeroSlide = isRandomExperience || isSearchExperience;
    //     if (hasHeroSlide && swiperInstance && typeof swiperInstance.slideTo === 'function') {
    //         swiperInstance.slideTo(activeIndex, 0);
    //     }
    // }, [isRandomExperience, isSearchExperience, swiperInstance, filteredItems.length, activeIndex]);

    // Auto-slide smooth da Hero (0) a prima carta (1) quando i dati sono pronti
    // Funziona SOLO per nuove richieste (non persistenza)
    const autoSlideToFirstCardRef = useRef(false); // Flag per evitare doppi slide

    useEffect(() => {
        // Condizioni per auto-slide:
        // 1. Swiper inizializzato e pronto
        // 2. Dati pronti (filteredItems.length > 0)
        // 3. È una nuova richiesta (non persistenza)
        // 4. Siamo sulla slide 0 (Hero)
        // 5. Non abbiamo già fatto lo slide (autoSlideToFirstCardRef)
        
        if (!swiperInstance || !swiperInstance.initialized) return;
        if (filteredItems.length === 0) return;
        // IMPORTANTE: Per similar, forziamo lo slide anche se loading è true (dopo filtering)
        if (currentType === 'similar' && isFilteringArtists) return; // Aspetta che il filtering finisca
        if (currentType !== 'similar' && loading) return; // Per altri tipi, aspetta loading
        if (activeIndex !== 0) return; // Deve essere sulla Hero
        if (autoSlideToFirstCardRef.current) return; // Già fatto lo slide
        
        const isNewRequest = (currentType === 'random' && isNewRandomRequestRef.current) ||
                            (currentType === 'similar' && isNewSimilarRequestRef.current);
        
        if (!isNewRequest) return; // Non è una nuova richiesta
        
        // Verifica che Swiper abbia renderizzato almeno 2 slide (Hero + prima carta)
        if (swiperInstance.slides && swiperInstance.slides.length < 2) {
            console.log(`[Auto-Slide] ⏳ Waiting for Swiper slides (current: ${swiperInstance.slides.length})`);
            return;
        }
        
        console.log(`[Auto-Slide] 🎯 New ${currentType} request ready, sliding from 0 to 1`);
        autoSlideToFirstCardRef.current = true; // Marca come fatto
        
        // Per similar, marca lo slide come non completato e forza lo slide IMMEDIATAMENTE
        if (currentType === 'similar') {
            similarSlideCompletedRef.current = false;
            // IMPORTANTE: Forza lo slide a 1 IMMEDIATAMENTE senza delay
            // Il loader overlay rimane visibile durante lo slide
            console.log(`[Auto-Slide] 🎯 Similar: Forcing immediate slide to 1`);
            swiperInstance.slideTo(1, 400); // 400ms per transizione smooth
        } else {
            // Per altri tipi, usa il delay normale
            setTimeout(() => {
                if (swiperInstance && swiperInstance.initialized && swiperInstance.slideTo) {
                    console.log(`[Auto-Slide] ✅ Executing smooth slideTo(1)`);
                    swiperInstance.slideTo(1, 400); // 400ms per transizione smooth
                }
            }, 300); // Piccolo delay per assicurarsi che tutto sia renderizzato
        }
        
        // Reset dei flag dopo lo slide
        if (currentType === 'random') {
            isNewRandomRequestRef.current = false;
        } else if (currentType === 'similar') {
            isNewSimilarRequestRef.current = false;
        }
    }, [swiperInstance, filteredItems.length, loading, currentType, activeIndex, isFilteringArtists]);
    
    // Reset autoSlideToFirstCardRef quando cambia il tipo o quando l'utente naviga manualmente
    useEffect(() => {
        autoSlideToFirstCardRef.current = false;
    }, [currentType]);
    
    // Reset quando l'utente naviga manualmente (non siamo più sulla 0)
    useEffect(() => {
        if (activeIndex !== 0) {
            autoSlideToFirstCardRef.current = false;
        }
    }, [activeIndex]);

    // Ripristina lo state di React Router dopo login (se salvato)
    // IMPORTANTE: aspetta che l'autenticazione sia processata prima di ripristinare lo state
    useEffect(() => {
        // Controlla sia pending_login_redirect che pending_router_state
        const savedRedirect = sessionStorage.getItem('pending_login_redirect');
        const routerStateKey = `pending_router_state_${window.location.pathname}`;
        const savedRouterState = sessionStorage.getItem(routerStateKey);
        
        // Se non c'è uno state salvato, non fare nulla
        if (!savedRedirect && !savedRouterState) {
            return;
        }
        
        console.log('[ShowResult] 🔍 Checking for saved state after login:', {
            hasRedirect: !!savedRedirect,
            hasRouterState: !!savedRouterState,
            currentPath: window.location.pathname,
            currentState: !!state,
            isAuthenticated: isAuthenticated
        });
        
        // Se l'utente è autenticato E non c'è state, ripristinalo
        // Aspetta che isAuthenticated sia true prima di ripristinare
        if (isAuthenticated && !state && (savedRedirect || savedRouterState)) {
            // Piccolo delay per assicurarsi che tutto sia pronto
            const timer = setTimeout(() => {
                try {
                    let routerState = null;
                    
                    // Prova prima con pending_router_state (più specifico)
                    if (savedRouterState) {
                        routerState = JSON.parse(savedRouterState);
                        console.log('[ShowResult] 📦 Found saved router state:', routerState);
                    } else if (savedRedirect) {
                        const redirectData = JSON.parse(savedRedirect);
                        if (redirectData.routerState) {
                            routerState = redirectData.routerState;
                            console.log('[ShowResult] 📦 Found router state in redirect data:', routerState);
                        }
                    }
                    
                    if (routerState && window.location.pathname === (routerState.pathname || window.location.pathname)) {
                        console.log('[ShowResult] 🔄 Restoring router state after login (user authenticated):', routerState);
                        
                        // Rimuovi i flag prima di navigare per evitare loop
                        sessionStorage.removeItem('pending_login_redirect');
                        if (savedRouterState) {
                            sessionStorage.removeItem(routerStateKey);
                        }
                        
                        // Naviga con lo state ripristinato
                        navigate(window.location.pathname, {
                            state: routerState,
                            replace: true
                        });
                    }
                } catch (e) {
                    console.error('[ShowResult] ❌ Failed to restore router state:', e);
                    sessionStorage.removeItem('pending_login_redirect');
                    if (savedRouterState) {
                        sessionStorage.removeItem(routerStateKey);
                    }
                }
            }, 200); // Piccolo delay per assicurarsi che tutto sia pronto
            
            return () => clearTimeout(timer);
        } else if (savedRedirect || savedRouterState) {
            // Se c'è già uno state O l'utente non è ancora autenticato, pulisci i flag solo se non servono più
            if (state) {
                console.log('[ShowResult] ✅ State already present, cleaning up saved flags');
                sessionStorage.removeItem('pending_login_redirect');
                if (savedRouterState) {
                    sessionStorage.removeItem(routerStateKey);
                }
            }
        }
    }, [state, navigate, isAuthenticated]); // Esegui quando state cambia, navigate cambia, o isAuthenticated cambia

    // Salva lo state di React Router quando cambia (per preservarlo durante login)
    useEffect(() => {
        if (state && state.type && (state.type === 'daily-discovery-artists' || state.type === 'top-artists' || state.type === 'similar')) {
            const routerStateKey = `pending_router_state_${window.location.pathname}`;
            try {
                sessionStorage.setItem(routerStateKey, JSON.stringify(state));
                console.log('[ShowResult] 💾 Saved router state for login recovery');
            } catch (e) {
                console.warn('[ShowResult] Failed to save router state:', e);
            }
        }
    }, [state]);

    // Gestisce il type al mount e ai cambiamenti - RESET IMMEDIATO
    useEffect(() => {
        console.log('[Random Persistence] 🔄 useEffect mount/type change', {
            hasState: !!state,
            stateType: state?.type,
            currentPath,
            currentType
        });
        
        // IMPORTANTE: resetta forceResetProcessedRef quando non c'è forceReset o quando si naviga senza state
        if (!state || !state.type || !state.forceReset) {
            forceResetProcessedRef.current = false;
        }
        
        // Se NON c'è state (arrivo diretto su una pagina), ripristina stati di default
        if (!state || !state.type) {
            console.log('[Random Persistence] 📌 No state detected, checking path...');
            // Pulisci solo lo stato legacy della sezione similar
            sessionStorage.removeItem('similar_artists');
            sessionStorage.removeItem('similar_index');
            sessionStorage.removeItem('similar_query');
            
            // Reset completo
            setCurrentType(null);
            setItems([]);
            setFilteredItems([]);
            setSearchQuery('');
            setCurrentSearchQuery('');
            setCurrentArtistImage(null);
            const isRandomPath = currentPath === '/random';
            const isSearchPath = currentPath === '/similar';
            console.log('[Persistence] 🎲 Path check:', { isRandomPath, isSearchPath });
            
            let hydrated = false;
            if (isRandomPath) {
                hydrated = hydrateRandomFromPersistence();
            } else if (isSearchPath) {
                hydrated = hydrateSearchFromPersistence();
            }
            
            console.log('[Persistence] 💧 Hydration result:', hydrated);
            if (!hydrated) {
                console.log('[Persistence] ⚠️ No hydration, resetting activeIndex to 0');
                setActiveIndex(0);
            }
            setLoading(false);
            setError(null);
            setSearchMessage('');
            setIsInputFocused(false);
            setHasSelectedFromDropdown(false);
            setSearchType('artist');
            setIsSearching(false);
            setFreeTextAnalysis(null);
            requestMade.current = false;
            
            // Se siamo su /similar senza state, mostra la pagina di ricerca
            if (pathname === '/similar') {
                setCurrentType('similar');
                console.log('[Search Persistence] 🎯 Setting currentType to similar (no state path)');
                // IMPORTANTE: se torni su /similar, resetta il flag per permettere l'hydration
                searchHydrationAttemptedRef.current = false;
            } else if (pathname === '/random') {
                setCurrentType('random');
                console.log('[Random Persistence] 🎯 Setting currentType to random (no state path)');
                // IMPORTANTE: se torni su /random, resetta il flag per permettere l'hydration
                randomHydrationAttemptedRef.current = false;
            }
            return;
        }
        
        // Se c'è il flag forceReset, resetta TUTTO
        // IMPORTANTE: evita di processare forceReset più volte per lo stesso state
        if (state.forceReset && !forceResetProcessedRef.current) {
            console.log('[Random Persistence] 🔥 forceReset detected, type:', state.type);
            forceResetProcessedRef.current = true; // Marca come processato
            
            // RESET CRITICO: Se il componente viene riutilizzato, dobbiamo resettare i flag di stato visuale
            // altrimenti Swiper potrebbe pensare di essere già pronto con i dati vecchi
            console.log('[ForceReset] 🧹 Resetting visual state (swiperReady, firstSlideReady)');
            setSwiperReady(false);
            setFirstSlideReady(false);
            if (swiperInstance) {
                // Distruggi o invalida swiper se possibile, o fidati che il render lo ricrei
                // Non possiamo distruggerlo qui, ma il setSwiperReady(false) nasconderà lo Swiper
            }

            // Pulisci sessionStorage legacy (solo similar)
            sessionStorage.removeItem('similar_artists');
            sessionStorage.removeItem('similar_index');
            sessionStorage.removeItem('similar_query');
            sessionStorage.removeItem('random_artists');
            sessionStorage.removeItem('random_index');
            
            // IMPORTANTE: Chiama l'hydration PRIMA di cambiare currentType per evitare re-render che puliscono gli items
            // IMPORTANTE: setta SOLO il flag del tipo corrente, non tutti i flag
            let hydrated = false;
            if (state.type === 'random') {
                randomHydrationAttemptedRef.current = true; // Blocca salvataggi durante hydration
                searchHydrationAttemptedRef.current = false; // Reset degli altri
                dailyHydrationAttemptedRef.current = false;
                console.log('[Random Persistence] 🎲 Attempting hydration in forceReset for random...');
                hydrated = hydrateRandomFromPersistence();
                console.log('[Random Persistence] 💧 Hydration result in forceReset:', hydrated);
            } else if (state.type === 'similar') {
                searchHydrationAttemptedRef.current = true; // Blocca salvataggi durante hydration
                randomHydrationAttemptedRef.current = false; // Reset degli altri
                dailyHydrationAttemptedRef.current = false;
                console.log('[Search Persistence] 🔍 Attempting hydration in forceReset for search...');
                hydrated = hydrateSearchFromPersistence();
                console.log('[Search Persistence] 💧 Hydration result in forceReset:', hydrated);
            } else if (state.type === 'daily-discovery-artists' || state.type === 'top-artists') {
                dailyHydrationAttemptedRef.current = true; // Blocca salvataggi durante hydration
                randomHydrationAttemptedRef.current = false; // Reset degli altri
                searchHydrationAttemptedRef.current = false;
            }
            
            // IMPORTANTE: setta currentType SOLO se è diverso, per evitare re-render inutili
            // Se l'hydration ha avuto successo, gli items sono già stati settati
            if (currentType !== state.type) {
                console.log(`[ForceReset] Changing currentType from ${currentType} to ${state.type}`);
            setCurrentType(state.type);
            } else {
                console.log(`[ForceReset] currentType already ${state.type}, skipping setCurrentType`);
            }
            
            // Se NON c'è stata hydration, resetta gli stati
            if (!hydrated) {
                console.log('[ForceReset] No hydration, resetting UI states');
            setSearchQuery('');
            setCurrentSearchQuery('');
            setCurrentArtistImage(null);
            setSearchMessage('');
            setIsInputFocused(false);
            setHasSelectedFromDropdown(false);
                setSearchType('artist');
            setIsSearching(false);
            setFreeTextAnalysis(null);
            } else {
                console.log('[ForceReset] Hydration successful, preserving UI states');
                // Preserva gli stati settati dall'hydration, ma assicurati che loading/error siano puliti
                setError(null);
            }
            
            setLoading(false);
            requestMade.current = false;
            
            if (state.type === 'random') {
                if (!hydrated) {
                    console.log('[Random Persistence] ⚠️ No hydration in forceReset, resetting everything');
                    setItems([]);
                    setFilteredItems([]);
                    setActiveIndex(0);
                    randomHydrationAttemptedRef.current = false;
                } else {
                    console.log('[Random Persistence] ✅ Hydration successful, items preserved');
                }
                // Se hydration ha successo, items/filteredItems/activeIndex sono già impostati
            } else if (state.type === 'similar') {
                if (!hydrated) {
                    // Se non c'è hydration ma state.items esiste, usalo per far partire il filtering
                    if (state.items && state.items.length > 0) {
                        console.log('[Search Persistence] 📦 Using state.items to populate items and start filtering', {
                            itemsCount: state.items.length
                        });
                        setItems(state.items);
                        setFilteredItems([]); // Vuoto per far partire il filtering
                        setSearchQuery('');
                        setCurrentSearchQuery(state.query || '');
                        setSearchType(state.searchType || 'artist');
                        if (state.freeTextAnalysis) {
                            setFreeTextAnalysis(state.freeTextAnalysis);
                        }
                        setActiveIndex(0);
                        setHasSearchPersistence(false);
                        setIsFilteringArtists(false);
                        setLoading(true);
                        searchHydrationAttemptedRef.current = false;
                    } else {
                    console.log('[Search Persistence] ⚠️ No hydration in forceReset, resetting everything');
                    setItems([]);
                    setFilteredItems([]);
                    setActiveIndex(0);
                    searchHydrationAttemptedRef.current = false;
                    }
                }
            } else if (state.type === 'daily-discovery-artists' || state.type === 'top-artists') {
                console.log('[Daily Persistence] 🔁 Attempting hydration in forceReset for daily...');
                const sourceItems = state.items || [];
                if (sourceItems.length) {
                    setItems(sourceItems);
                    setFilteredItems(sourceItems);
                    const hydrated = hydrateDailyFromPersistence(sourceItems);
                    console.log('[Daily Persistence] 💧 Hydration result in forceReset:', hydrated);
                    if (!hydrated) {
                        setActiveIndex(0);
                        dailyHydrationAttemptedRef.current = false;
                    }
                } else {
                    setItems([]);
                    setFilteredItems([]);
                    setActiveIndex(0);
                    dailyHydrationAttemptedRef.current = false;
                }
            } else {
                setItems([]);
                setFilteredItems([]);
                setActiveIndex(0);
                randomHydrationAttemptedRef.current = false;
                searchHydrationAttemptedRef.current = false;
                dailyHydrationAttemptedRef.current = false;
            }
            return;
        }
        
        // IMPORTANTE: resetta il flag forceReset quando state cambia (nuovo state = nuovo forceReset)
        // Se non c'è forceReset, resetta sempre il flag
        // Se c'è forceReset ma il type è cambiato, resetta il flag
        // Se c'è forceReset e il type è lo stesso ma state.timestamp è cambiato, resetta il flag (nuova navigazione)
        if (!state?.forceReset) {
            forceResetProcessedRef.current = false;
        } else if (state.type && state.type !== currentType) {
            forceResetProcessedRef.current = false;
        } else if (state.timestamp && state.timestamp !== (state._lastProcessedTimestamp || 0)) {
            // Nuova navigazione con forceReset - resetta il flag
            forceResetProcessedRef.current = false;
        }
        
        // SEMPRE controlla state.type per resettare quando necessario
        if (state.type) {
            // Se il type è cambiato O se state.timestamp è cambiato (nuova navigazione)
            const typeChanged = !currentType || state.type !== currentType;
            
            if (typeChanged) {
                debugLog(`[ShowResult] 🔄 TYPE CHANGE: ${currentType || 'null'} → ${state.type}`);
                
                // Reset stato base
                requestMade.current = false;
                setCurrentType(state.type);
                setSearchQuery('');
                setCurrentSearchQuery('');
                setCurrentArtistImage(null);
                setError(null);
                setSearchMessage('');
                setIsInputFocused(false);
                setHasSelectedFromDropdown(false);
                
                firstSlideReadyRef.current = false;
                setFirstSlideReady(false);
                
                // Items: logica PER TIPO
                if (state.type === 'random') {
                    console.log('[Random Persistence] 🎲 typeChanged → random, attempting hydration...');
                    debugLog(`[ShowResult] 📥 Random - hydrate from persistence or show intro`);
                    const hydrated = hydrateRandomFromPersistence();
                    console.log('[Random Persistence] 💧 Hydration result in typeChanged:', hydrated);
                    if (!hydrated) {
                        console.log('[Random Persistence] ⚠️ No hydration in typeChanged, resetting activeIndex');
                        setActiveIndex(0);
                    }
                    firstSlideReadyRef.current = false;
                } else if (state.items && state.items.length > 0) {
                    // Altri tipi: carica dallo state
                    console.log(`[ShowResult] 📥 Loading ${state.items.length} items from state (${state.type})`);
                    debugLog(`[ShowResult] 📥 Loading ${state.items.length} items from state (${state.type})`);
                    // IMPORTANTE: imposta loading PRIMA di svuotare filteredItems (evita skeleton)
                    firstSlideReadyRef.current = false;
                    setFirstSlideReady(false);
                    if (state.type === 'top-artists') {
                        // Top-artists: mostra subito (già verificati da LastFM)
                        setItems(state.items);
                        setFilteredItems(state.items);
                        setLoading(false);
                    } else if (state.type === 'daily-discovery-artists') {
                        // Daily: mostra subito (già verificati, non serve filtraggio)
                        console.log('[ShowResult] 📥 Daily-discovery-artists: setting items and filteredItems directly');
                        setItems(state.items);
                        setFilteredItems(state.items);
                        setLoading(false);
                    } else if (state.type === 'similar') {
                        // Similar: devono filtrare - mostra loader SUBITO
                        console.log('[ShowResult] 📥 Similar: setting items from state, filtering will start');
                        setLoading(true); // Mostra loader PRIMA
                        setItems(state.items);
                        setFilteredItems([]); // Sarà popolato dal useEffect di filtraggio
                        // Imposta anche query e freeTextAnalysis se presenti
                        if (state.query) {
                            setSearchQuery('');
                            setCurrentSearchQuery(state.query);
                        }
                        if (state.freeTextAnalysis) {
                            setFreeTextAnalysis(state.freeTextAnalysis);
                        }
                        if (state.searchType) {
                            setSearchType(state.searchType);
                        }
                    } else {
                        // Altri tipi: devono filtrare
                        setLoading(true);
                        setItems(state.items);
                        setFilteredItems([]);
                    }
                } else {
                    // Nessun item
                    setItems([]);
                    setFilteredItems([]);
                    setActiveIndex(0);
                    setLoading(false);
                }
                return;
            }
        }
        
        // Preserva searchType dallo state se presente
        if (state?.searchType) {
            setSearchType(state.searchType);
        }
    }, [state?.type, state?.forceReset, state?.timestamp, state?.searchType, state?.items, currentType, hydrateRandomFromPersistence]);

    useEffect(() => {
        // Per random non serve query, per similar serve query
        // Supporta sia state?.type che currentType (per arrivi diretti via URL)
        let typeToUse = state?.type || currentType;
        
        // Fallback al pathname se type è ancora null
        if (!typeToUse) {
            const path = currentPath;
            if (path === '/random') typeToUse = 'random';
            else if (path === '/similar') typeToUse = 'similar';
            else if (path.includes('top-artists')) typeToUse = 'top-artists';
        }
        
        debugLog('📊 [useEffect] Check:', {
            requestMade: requestMade.current,
            typeToUse,
            itemsLength: items.length,
            stateQuery: state?.query,
            pathname: currentPath,
            loading
        });
        
        // Chiama handleRequest se necessario (SOLO se items vuoti)
        // IMPORTANTE: Se siamo in loading, è il momento giusto per chiamare il bridge
        if (!requestMade.current && typeToUse && items.length === 0) {
            if (typeToUse === 'random') {
                debugLog('ℹ️ [useEffect] Random waiting for explicit user action');
                return;
            } else if (typeToUse === 'similar' && state?.query) {
                // SIMILAR: chiama se c'è una query
                debugLog('✅ [useEffect] Calling handleRequest for SIMILAR');
                requestMade.current = true;
                handleRequest(typeToUse, state?.query || '');
            } else if (typeToUse === 'top-artists') {
                // TOP ARTISTS
                debugLog('✅ [useEffect] Calling handleRequest for TOP-ARTISTS');
                requestMade.current = true;
                handleRequest(typeToUse, '');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.type, state?.query, currentType, items.length, loading]);



    const handleSlideReady = useCallback((position) => {
        console.log(`🖼️ [handleSlideReady] Called with position: ${position}`);
        // IMPORTANTE: per random/similar, la slide 0 è la hero slide (senza artista)
        // La prima slide con artista è la slide 1, quindi aspettiamo quella!
        const isRandomOrSimilar = currentType === 'random' || currentType === 'similar';
        const targetPosition = isRandomOrSimilar ? 1 : 0;
        
        if (position === targetPosition && !firstSlideReadyRef.current) {
            const timestamp = performance.now();
            console.log(`🖼️ [handleSlideReady] ✅ FIRST ARTIST SLIDE (position ${targetPosition}) IS READY! (timestamp: ${timestamp.toFixed(0)}ms)`);
            firstSlideReadyRef.current = true;
            setFirstSlideReady(true);
            // Non spegnere il loader qui - lo fa il useEffect che controlla tutte le condizioni
        } else {
            console.log(`🖼️ [handleSlideReady] Slide ${position} ready (not target slide ${targetPosition} or already notified)`);
        }
    }, [currentType]);

    // Per random, il loader è gestito direttamente in handleRequest (2.5s fisso)
    // Questo useEffect gestisce solo gli altri tipi
    useEffect(() => {
        if (currentType === 'random') {
            // Per random, non fare nulla qui - il loader è gestito in handleRequest con 2.5s fisso
            return;
        }
        
        const hasItems = filteredItems.length > 0;
        const isHeroOnly = (isRandomExperience || isSearchExperience) && filteredItems.length === 0 && !loading && !isFiltering;
        
        if (loading) {
            // IMPORTANTE: Per Similar, il loader deve rimanere acceso fino a quando non facciamo lo slide alla slide 1
            if (currentType === 'similar') {
                if (isFilteringArtists) {
                    console.log('⏱️ [LOADER CHECK] ⏳ Similar: filtering in progress, keeping loader ON');
                    return; // Mantieni il loader acceso durante filtering
                }
                // Se abbiamo items ma siamo ancora sulla hero (activeIndex === 0), mantieni il loader
                // Il loader scomparirà quando facciamo lo slide alla slide 1 (activeIndex > 0)
                if (hasItems && activeIndex === 0) {
                    console.log('⏱️ [LOADER CHECK] ⏳ Similar: items ready but still on hero (activeIndex=0), keeping loader ON until slide');
                    return; // Mantieni il loader fino a quando non facciamo lo slide
                }
                // Se abbiamo fatto lo slide (activeIndex > 0), spegni il loader
                if (hasItems && activeIndex > 0 && swiperReady) {
                    console.log('⏱️ [LOADER CHECK] ✅ Similar: items ready and slid to first card (activeIndex>0), turning off loader');
                    setLoading(false);
                }
                return; // Non processare altri casi per Similar
            }
            
            // Altri tipi (non Similar)
            if (isHeroOnly && swiperReady && !isFilteringArtists) {
                // Hero-only: basta che Swiper sia pronto E non stiamo filtrando
                console.log('⏱️ [LOADER CHECK] ✅ Hero-only mode, Swiper ready, turning off loader');
                setLoading(false);
            } else if (hasItems && swiperReady && !isFilteringArtists) {
                // Altri tipi: basta che Swiper sia pronto E non stiamo filtrando
                console.log('⏱️ [LOADER CHECK] ✅ Similar/Daily type, Swiper ready, turning off loader');
                setLoading(false);
            }
        }
    }, [filteredItems.length, swiperReady, loading, isRandomExperience, isSearchExperience, currentType, isFilteringArtists, activeIndex]);
    
    // Reset swiperReady e firstSlideReady quando cambia il tipo
    // IMPORTANTE: NON resettare firstSlideReady qui, viene resettato solo quando inizi una nuova generazione
    useEffect(() => {
        setSwiperReady(false);
        // NON resettare firstSlideReady qui - viene resettato solo in handleRequest quando inizi una nuova generazione
    }, [currentType]);
    
    // Quando filteredItems viene popolato, Swiper si aggiorna automaticamente
    // Se filteredItems è popolato, Swiper ha già renderizzato le slide, quindi è pronto
    // SOLO per random: auto-set swiperReady quando filteredItems è popolato
    useEffect(() => {
        if (currentType === 'random' && filteredItems.length > 0 && !swiperReady) {
            console.log('🔄 [SWIPER CHECK] filteredItems is populated, Swiper must be ready (setting swiperReady = true)');
            setSwiperReady(true);
        }
    }, [filteredItems.length, currentType, swiperReady]);
    
    // Quando la prima slide è pronta (Spotify ha risposto), Swiper è sicuramente pronto
    useEffect(() => {
        if (currentType === 'random' && firstSlideReady && !swiperReady) {
            console.log('🔄 [SWIPER CHECK] First slide is ready (Spotify responded), Swiper must be ready too');
            setSwiperReady(true);
        }
    }, [firstSlideReady, currentType, swiperReady]);
    
    // Traccia quando loading cambia (solo per random)
    useEffect(() => {
        if (currentType === 'random' || pathname === '/random') {
            console.log(`🔄 [LOADING STATE CHANGE] loading = ${loading}`);
        }
    }, [loading, currentType, pathname]);

    // Salva la lista quando cambia (nuova generazione o filtri)
    // IMPORTANTE: NON settare hasRandomPersistence a true se è una nuova richiesta
    // (altrimenti l'auto-slide non funziona)
    useEffect(() => {
        if (currentType === 'random' && filteredItems.length > 0) {
            console.log('[Random Persistence] 💾 Saving filteredItems:', filteredItems.length);
            // IMPORTANTE: Leggi lo stato corrente per preservare activeIndex già salvato
            const currentState = PersistenceService.getRandomState();
            PersistenceService.setRandomState({
                ...currentState,
                items: filteredItems
            });
            // Setta hasRandomPersistence SOLO se NON è una nuova richiesta
            // (altrimenti l'auto-slide non può distinguere tra nuova richiesta e persistenza)
            if (!isNewRandomRequestRef.current) {
                setHasRandomPersistence(true);
            }
        }
    }, [currentType, filteredItems]);

    // Salva la lista e query Search quando cambiano
    // IMPORTANTE: NON settare hasSearchPersistence a true finché il filtering non è completato
    // (altrimenti il loader viene nascosto troppo presto)
    useEffect(() => {
        if (currentType === 'similar' && filteredItems.length > 0 && currentSearchQuery && !isFilteringArtists) {
            console.log('[Search Persistence] 💾 Saving search results:', {
                itemsCount: filteredItems.length,
                query: currentSearchQuery,
                searchType,
                isFilteringArtists
            });
            // IMPORTANTE: Leggi lo stato corrente per preservare activeIndex già salvato
            const currentState = PersistenceService.getSearchState();
            PersistenceService.setSearchState({
                ...currentState,
                items: filteredItems,
                query: currentSearchQuery,
                searchType,
                freeTextAnalysis: freeTextAnalysis || null
            });
            // IMPORTANTE: Setta hasSearchPersistence SOLO quando il filtering è completato
            // (non durante il filtering, altrimenti il loader viene nascosto)
            setHasSearchPersistence(true);
        }
    }, [currentType, filteredItems, currentSearchQuery, searchType, freeTextAnalysis, isFilteringArtists]);

    // RIMOSSO: il salvataggio ora avviene direttamente in onSlideChange dello Swiper

    // NO cache per random e similar - solo fresh

    const handleRequest = async (type, query = '') => {
        console.log(`📡 [handleRequest] ===== START REQUEST type: ${type} =====`);
        console.log(`📡 [handleRequest] Current state before:`, {
            currentType,
            loading,
            itemsLength: items.length,
            filteredItemsLength: filteredItems.length
        });
        
        // Safety: assicurati che currentType sia allineato
        if (currentType !== type) {
            console.log(`📡 [handleRequest] ⚠️ currentType mismatch! ${currentType} → ${type}`);
            setCurrentType(type);
        }
        
        console.log(`📡 [handleRequest] 🔄 About to call setLoading(true)...`);
        setLoading(true);
        console.log(`📡 [handleRequest] ✅ setLoading(true) called!`);
        
        // Verifica che loading sia stato settato (con un piccolo delay per React)
        setTimeout(() => {
            console.log(`📡 [handleRequest] 🔍 Loading state after setLoading(true):`, loading);
        }, 10);
        
        setError(null);
        setSearchMessage(''); // Pulisce i messaggi precedenti
        
        try {
            let results = [];
            
            if (type === 'similar' && query) {
                const cacheResult = await ArtistCacheService.getSimilarArtists(query);
                
                // Converte formato cache in formato LastFM
                results = cacheResult.artists
                    .map(artist => ({
                        name: artist.name,
                        match: (artist.similarity / 100).toString(), // 95 -> "0.95"
                        url: `https://www.last.fm/music/${encodeURIComponent(artist.name)}`,
                        image: []
                    }))
                    // Rimuovi duplicati (Gemini a volte restituisce lo stesso artista più volte)
                    .filter((artist, index, self) => 
                        index === self.findIndex(a => a.name.toLowerCase() === artist.name.toLowerCase())
                    )
                    .slice(0, 50);
                
                debugLog(`[ShowResult] Source: ${cacheResult.source || 'unknown'} for "${query}" (${results.length} unique artists)`);
                
                // Messaggio se non trova risultati per similar
                if (results.length === 0) {
                    setSearchMessage(`No artists found with the name "${query}". Please try a new search with a different artist name.`);
                    setLoading(false);
                    return;
                }
            } else if (type === 'random') {
                // RANDOM: sempre resetta items e chiama LastFM per nuovi artisti
                console.log('🎲 [handleRequest] Resetting items and filteredItems');
                setItems([]);
                setFilteredItems([]);
                setSwiperReady(false);
                setFirstSlideReady(false);
                firstSlideReadyRef.current = false;
                
                // NO LOADER per random
                setLoading(false);
                
                const startTime = performance.now();
                console.log('🎲 [handleRequest] 📞 Calling getRandomArtists(30)...');
                debugLog('✅ [handleRequest] Calling getRandomArtists for RANDOM');
                results = await getRandomArtists(30); // 30 artisti per random
                const elapsed = performance.now() - startTime;
                console.log(`🎲 [handleRequest] ✅ Random artists received: ${results.length} (took ${elapsed.toFixed(0)}ms)`);
                debugLog(`✅ [handleRequest] Random artists received: ${results.length}`);
                
                // Messaggio se non trova risultati per random (molto improbabile)
                if (results.length === 0) {
                    setSearchMessage('No random artists found. Please try again later.');
                    setIsGeneratingRandom(false);
                    return;
                }
                
                // IMPORTANTE: reset activeIndex a 0 per random per evitare di andare all'ultima slide
                setActiveIndex(0);
            } else if (type === 'top-artists') {
                // Prima prova con IP, poi fallback alla lingua del browser
                let detectedCountry = currentCountry;
                
                if (!detectedCountry) {
                    try {
                        detectedCountry = await getCountryFromIP();
                    } catch (error) {
                        console.error('Errore rilevamento IP, uso lingua browser:', error);
                        detectedCountry = getCountryFromLanguage();
                    }
                }
                
                const topResults = await getTopArtists(20, detectedCountry); // 20 artisti per chart
                
                // Salva il paese corrente per il pulsante globale e in localStorage
                if (!currentCountry) {
                    setCurrentCountry(detectedCountry);
                    try {
                        localStorage.setItem('detectedCountry', detectedCountry);
                    } catch (e) {
                        debugWarn('Non posso salvare in localStorage:', e);
                    }
                }
                
                // Aggiungi ranking agli artisti
                results = topResults.map((artist, index) => ({
                    ...artist,
                    rank: index + 1
                }));
                
                // Messaggio se non trova risultati per top artists
                if (results.length === 0) {
                    setSearchMessage('No top artists found. Please try again later.');
                    setLoading(false);
                    return;
                }
            } else if (type === 'daily-discovery' || type === 'daily-discovery-artists') {
                // Daily Discovery: usa gli artisti passati direttamente dallo state
                // IMPORTANTE: gli artisti vengono passati come 'items', non 'artists'
                results = state.items || state.artists || [];
                
                debugLog(`[ShowResult] Daily Discovery - Received ${results.length} artists:`, results.map(a => typeof a === 'string' ? a : a.name));
                
                if (results.length === 0) {
                    setSearchMessage('No artists available for this discovery.');
                    setLoading(false);
                    return;
                }
            }
            
            console.log(`📡 [handleRequest] ✅ Setting items (${results.length} items)`);
            setItems(results);
            setSearchMessage(''); // Pulisce il messaggio se tutto va bene
            
            // IMPORTANTE: per random, assicurati che activeIndex sia sempre 0
            if (type === 'random') {
                console.log('🎲 [handleRequest] Setting activeIndex = 0');
                setActiveIndex(0);
                // NO LOADER per random
                setLoading(false);
            } else if (type === 'daily-discovery-artists' || type === 'similar') {
                // Daily/Search: il loader rimane acceso durante il filtraggio parallelo
                // (il useEffect di filtraggio lo spegnerà quando filteredItems è pronto)
                // NON spegnere qui!
            } else {
                // Altri tipi: spegni il loader
                setLoading(false);
            }
        } catch (err) {
            console.error('[handleRequest] Error:', err);
            setSearchMessage(getServerErrorMessage(language));
            setError(null);
            setCurrentType(null);
            setLoading(false); // In caso di errore, spegni il loader
            setIsGeneratingRandom(false);
        }
    };

    const triggerRandomGeneration = () => {
        console.log('🎲 [RANDOM] ===== CLICK GENERA CONSIGLI =====');
        console.log('🎲 [RANDOM] Current state:', {
            loading,
            itemsLength: items.length,
            filteredItemsLength: filteredItems.length,
            swiperReady,
            firstSlideReady,
            currentType
        });
        
        if (loading || isGeneratingRandom) {
            console.log('🎲 [RANDOM] ⚠️ Already loading, ignoring click');
            return;
        }
        
        console.log('🎲 [RANDOM] Starting generation...');
        setIsGeneratingRandom(true);
        PersistenceService.clearRandomState();
        setHasRandomPersistence(false);
        pendingRandomAutoSlideRef.current = true;
        isNewRandomRequestRef.current = true; // Marca come nuova richiesta
        setActiveIndex(0);
        requestMade.current = true;
        handleRequest('random', '').catch((err) => {
            console.error('🎲 [RANDOM] Generation failed:', err);
            setIsGeneratingRandom(false);
        });
    };

    // All'apertura di /random senza risultati salvati, genera subito (niente click sulla hero)
    useEffect(() => {
        if (!isRandomExperience) {
            randomAutoStartedRef.current = false;
            return;
        }
        if (randomAutoStartedRef.current) return;
        if (loading || isGeneratingRandom) return;
        if (items.length > 0 || filteredItems.length > 0 || hasRandomPersistence) return;

        randomAutoStartedRef.current = true;
        triggerRandomGeneration();
    }, [isRandomExperience, loading, isGeneratingRandom, items.length, filteredItems.length, hasRandomPersistence]);

    // State per tracciare se l'artista è stato selezionato dal dropdown
    const [hasSelectedFromDropdown, setHasSelectedFromDropdown] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);

    // Filtra gli artisti verificando su Spotify quali esistono
    // ECCEZIONE: random e top-artists vengono già verificati o passati direttamente
    // Filtriamo solo: similar, daily-discovery, free-text (da Gemini)
    useEffect(() => {
        console.log(`🔍 [FILTER useEffect] Check for ${currentType}:`, {
            itemsLength: items.length,
            filteredItemsLength: filteredItems.length,
            isFilteringArtists,
            currentType,
            hasSearchPersistence
        });
        
        // IMPORTANTE: Se c'è persistenza attiva (hydration completata), NON pulire filteredItems
        // anche se items.length === 0 (potrebbe essere un problema di timing con React batching)
        const hasActivePersistence = (currentType === 'random' && hasRandomPersistence) ||
                                     (currentType === 'similar' && hasSearchPersistence) ||
                                     (currentType === 'daily-discovery-artists' && hasDailyPersistence);
        
        // IMPORTANTE: Se l'hydration è in corso (flag true), NON pulire filteredItems
        // perché l'hydration sta per settare items e filteredItems
        const isHydrating = (currentType === 'random' && randomHydrationAttemptedRef.current) ||
                           (currentType === 'similar' && searchHydrationAttemptedRef.current) ||
                           (currentType === 'daily-discovery-artists' && dailyHydrationAttemptedRef.current);
        
        // Solo se abbiamo items e non stiamo già filtrando
        if (items.length === 0 || isFilteringArtists) {
            if (items.length === 0 && !hasActivePersistence && !isHydrating) {
                console.log(`🔍 [FILTER useEffect] No items, clearing filteredItems`);
                setFilteredItems([]);
            } else if (items.length === 0 && (hasActivePersistence || isHydrating)) {
                console.log(`🔍 [FILTER useEffect] No items but persistence/hydration active, skipping clear (timing issue)`);
            } else {
                console.log(`🔍 [FILTER useEffect] Already filtering, skipping`);
            }
            return;
        }

        // ECCEZIONE: per random, top-artists, daily-discovery-artists usa direttamente gli items (già verificati a monte)
        if ((currentType === 'random' || currentType === 'top-artists' || currentType === 'daily-discovery-artists') && items.length > 0) {
            const timestamp = performance.now();
            console.log(`⚡ [useEffect FILTER] Fast path for ${currentType} - setting filteredItems (${items.length} items)`);
            console.log(`⚡ [useEffect FILTER] Timestamp: ${timestamp.toFixed(0)}ms`);
            debugLog(`[ShowResult] ⚡ Fast path for ${currentType} - using ${items.length} items directly`);
            setFilteredItems(items);
            if (currentType === 'random') {
                setIsGeneratingRandom(false);
            }
            // NON spegnere il loader qui - aspetta che Swiper sia pronto E la prima slide sia visibile
            // Il loader verrà spento dal useEffect che controlla swiperReady + firstSlideReady
            console.log(`⚡ [useEffect FILTER] Loader will stay ON until Swiper + first slide ready`);
            return;
        }
        
        // Se non ci sono items, non fare nulla (aspetta che vengano caricati)
        if (items.length === 0) {
            setFilteredItems([]);
            return;
        }
        
        // ECCEZIONE: se filteredItems è già popolato (hydration), non rifare il filtering
        // SOLO se è hydration (hasSearchPersistence) e filteredItems ha lo stesso numero di items
        if (currentType === 'similar' && hasSearchPersistence && filteredItems.length > 0 && filteredItems.length === items.length) {
            console.log(`⚡ [useEffect FILTER] Skipping filter for similar - hydration detected (${filteredItems.length} items)`);
            
            // IMPORTANTE: Sblocca Swiper se non è pronto, altrimenti si crea un deadlock
            // (il loader aspetta Swiper, ma Swiper non renderizza perché c'è il loader)
            if (!swiperReady) {
                console.log('[ShowResult] 🔄 Forcing swiperReady for hydrated content');
                setTimeout(() => setSwiperReady(true), 100);
            }
            return;
        }
        
        console.log(`🔍 [FILTER useEffect] ✅ Starting filter for ${currentType} with ${items.length} items`);

        // Flag per interrompere il filtraggio quando cambia pagina/tipo
        let isCancelled = false;
        
        const filterArtists = async () => {
            const filteringStart = performance.now();
            console.log(`[ShowResult] 🔍 START Filtering ${items.length} artists on Spotify...`);
            setIsFilteringArtists(true);
            setLoading(true);
            setFilteredItems([]); // Nascondi tutto durante il filtraggio
            debugLog(`[ShowResult] 🔍 START Parallel filtering ${items.length} artists on Spotify (type: ${currentType})...`);
            
            // Timeout di sicurezza: se il filtering impiega più di 60 secondi, forza la fine
            let timeoutId;
            const timeoutPromise = new Promise((resolve) => {
                timeoutId = setTimeout(() => {
                    if (!isCancelled) {
                        debugWarn(`[ShowResult] ⏱️ Filtering timeout after 60s - showing available artists`);
                        resolve('timeout');
                    }
                }, 60000);
            });
            
            // OTTIMIZZATO: batch più grande e delay ridotto per massima velocità
            const BATCH_SIZE = 50; // Aumentato da 30 a 50 per più parallelismo
            const validArtists = [];
            let hasRateLimitError = false;
            
            for (let i = 0; i < items.length; i += BATCH_SIZE) {
                if (isCancelled) {
                    debugLog(`[ShowResult] 🛑 Filtering CANCELLED`);
                    setIsFilteringArtists(false);
                    setLoading(false);
                    return;
                }
                
                const batch = items.slice(i, i + BATCH_SIZE);
                debugLog(`[ShowResult] 🔄 Processing batch ${Math.floor(i/BATCH_SIZE) + 1}/${Math.ceil(items.length/BATCH_SIZE)} (${batch.length} artists)`);
                
                // Verifica tutti gli artisti del batch IN PARALLELO (Promise.allSettled per non bloccare su errori)
                const batchResults = await Promise.allSettled(
                    batch.map(async (artist) => {
                        if (!artist || !artist.name) return null;
                        
                        try {
                            // Durante il filtering, verifica solo se l'artista esiste (NON caricare album)
                            // Gli album verranno caricati quando la slide viene renderizzata
                            // skipAlbums=true per risparmiare chiamate API e evitare rate limit
                            const spotifyArtist = await searchArtistForProvider(
                                artist.name,
                                null,
                                artist.genre,
                                artist.origin,
                                true // skipAlbums: NON caricare album durante filtering
                            );
                            
                            // Se trovato, ritorna solo i dati base (senza album per risparmiare chiamate API)
                            if (spotifyArtist && spotifyArtist.id) {
                                return artist; // Ritorna l'artista se trovato
                            } else {
                                return null;
                            }
                        } catch (error) {
                            // Gestisci rate limiting (429) - continua ma logga
                            if (error.status === 429 || error.message?.includes('429') || error.response?.status === 429) {
                                hasRateLimitError = true;
                                debugWarn(`[ShowResult] Rate limit (429) checking "${artist.name}" - skipping`);
                            } else {
                            debugWarn(`[ShowResult] Error checking "${artist.name}":`, error.message);
                            }
                            return null;
                        }
                    })
                );
                
                // Estrai solo gli artisti validi dai risultati
                const validFromBatch = batchResults
                    .filter(result => result.status === 'fulfilled' && result.value !== null)
                    .map(result => result.value);
                
                validArtists.push(...validFromBatch);
                
                // Delay ridotto a 10ms (era 25ms) per velocità massima
                if (i + BATCH_SIZE < items.length && !isCancelled) {
                    await new Promise(resolve => setTimeout(resolve, 10));
                }
            }
            
            // Attendi che il filtering finisca O che scatti il timeout
            const result = await Promise.race([
                Promise.resolve('completed'),
                timeoutPromise
            ]);
            
            if (result === 'timeout') {
                // Timeout scattato - mostra almeno gli artisti validi trovati finora
                clearTimeout(timeoutId);
                debugWarn(`[ShowResult] ⏱️ Filtering timeout - showing ${validArtists.length} verified artists`);
                if (validArtists.length > 0) {
                    setFilteredItems(validArtists);
                } else if (items.length > 0) {
                    // Se non abbiamo artisti validi, mostra i primi items come fallback
                    debugWarn(`[ShowResult] ⚠️ No valid artists, showing first ${Math.min(10, items.length)} items as fallback`);
                    setFilteredItems(items.slice(0, Math.min(10, items.length)));
                }
                setIsFilteringArtists(false);
                setLoading(false);
                return;
            }
            
            if (!isCancelled) {
                clearTimeout(timeoutId); // Cancella timeout se completato
                debugLog(`[ShowResult] ✅ COMPLETED Parallel filtering: ${validArtists.length}/${items.length} artists verified`);
                if (hasRateLimitError) {
                    debugWarn(`[ShowResult] ⚠️ Rate limit errors encountered during filtering`);
                }
                // Se non ci sono artisti validi ma ci sono items, mostra almeno i primi items (fallback)
                if (validArtists.length === 0 && items.length > 0) {
                    debugWarn(`[ShowResult] ⚠️ No valid artists found, showing first ${Math.min(10, items.length)} items as fallback`);
                    setFilteredItems(items.slice(0, Math.min(10, items.length)));
                } else {
                setFilteredItems(validArtists); // MOSTRA TUTTI insieme
                }
                setIsFilteringArtists(false);
                setLoading(false);
                const filteringElapsed = performance.now() - filteringStart;
                console.log(`[Performance] Filtering artists completed in ${filteringElapsed.toFixed(0)} ms`);
                console.log(`[ShowResult] ✅ Filtering completed: ${validArtists.length} artists ready`);
                // Quando il filtering finisce, Swiper dovrebbe aggiornarsi automaticamente
                // Se Swiper è già inizializzato, forzalo come pronto per spegnere il loader
                // Altrimenti aspetta che Swiper si aggiorni (ma non troppo tempo)
                if (swiperReady) {
                    console.log(`[ShowResult] ✅ Swiper already ready, loader should turn off`);
                } else {
                    console.log(`[ShowResult] ⚠️ Swiper not ready yet, will wait for it to update`);
                    // Forza swiperReady dopo un breve delay per dare tempo a Swiper di aggiornarsi
                    setTimeout(() => {
                        if (!swiperReady) {
                            console.log(`[ShowResult] 🔄 Forcing swiperReady after filtering completion`);
                            setSwiperReady(true);
                        }
                    }, 500);
                }
                // Se Swiper è già inizializzato, forzalo come pronto per spegnere il loader
                if (swiperReady) {
                    console.log(`[ShowResult] ✅ Swiper already ready, loader should turn off`);
                } else {
                    console.log(`[ShowResult] ⚠️ Swiper not ready yet, will wait for it`);
                }
            }
        };

        filterArtists();
        
        // Cleanup: interrompi il filtraggio quando currentType cambia
        return () => {
            isCancelled = true;
            setIsFilteringArtists(false);
            // Il timeout verrà pulito automaticamente quando la funzione completa
        };
    }, [items, currentType]); // Esegui quando items o currentType cambia

    // Gestisce il click fuori dal wrapper per resettare isInputFocused
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchWrapperRef.current && 
                !searchWrapperRef.current.contains(event.target)) {
                setIsInputFocused(false);
            }
        };
        
        if (isInputFocused) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isInputFocused]);

    // Funzione per gestire il submit del testo libero (Foundly 2.0)
    const resetSearchState = useCallback(async () => {
        setSearchMessage('');
        setError(null);
        setBridgeMessage('');
        setCurrentPrompt('');
        setCurrentSearchQuery('');
        setItems([]);
        setFilteredItems([]);
        setLoading(false);
        setIsFilteringArtists(false);
        setIsSearching(false);
        setHasSearchPersistence(false);
        setHasRandomPersistence(false);
        setHasDailyPersistence(false);
        setCurrentType(null);
        setActiveIndex(0);
        await wakeLockService.release();
    }, [wakeLockService]);

    const handleFreeTextSubmit = async (text, isVoice = false) => {
        const trimmedText = typeof text === 'string' ? text.trim() : '';
        if (!trimmedText) {
            return;
        }

        console.log('[handleFreeTextSubmit] 🚀 START - User clicked submit');
        
        // IMPORTANTE: Reset tutto per nuova ricerca
        setIsSearching(true);
        setCurrentPrompt(trimmedText); // Salva il prompt (mostrato in corsivo)
        setSearchQuery(''); // Input si svuota e resta fine
        setSearchMessage('');
        setFreeTextAnalysis(null);
        setIsVoiceTranscript(isVoice); // Salva se è trascrizione vocale
        bridgeRequestIdRef.current += 1;
        const currentBridgeRequestId = bridgeRequestIdRef.current;
        setBridgeMessage('');
        setItems([]); // Pulisci items precedenti
        setFilteredItems([]); // Pulisci filteredItems precedenti
        setHasSearchPersistence(false); // Reset persistenza
        isNewSimilarRequestRef.current = true; // Marca come nuova richiesta
        similarSlideCompletedRef.current = false; // Reset flag slide completato per nuova ricerca
        setActiveIndex(0); // Torna alla Hero
        setIsInputFocused(false); // IMPORTANTE: Rimuovi focus quando parte la ricerca

        // 🔒 WAKE LOCK: Mantieni schermo attivo durante ricerca
        await wakeLockService.request();
        
        // IMPORTANTE: Setta loading=true SUBITO (PRIMA di qualsiasi chiamata API) per mostrare il loader immediatamente
        console.log('[handleFreeTextSubmit] ⚡ Setting loading=true IMMEDIATELY');
        setLoading(true);
        setError(null);
        
        // STEP 1: Aspetta validazione bridge (1-2 secondi)
        console.log('[ShowResult] ⏳ Aspetto validazione bridge...');
        const bridgeStart = performance.now();
        const bridgeResult = await getFreeTextBridgeMessage(trimmedText, language);
        const bridgeElapsed = performance.now() - bridgeStart;
        console.log(`[Performance] Bridge completed in ${bridgeElapsed.toFixed(0)} ms`);
        
        // Se il bridge restituisce errore di validazione, blocca tutto IMMEDIATAMENTE
        if (bridgeResult && typeof bridgeResult === 'object' && bridgeResult.valid === false) {
            console.log('[ShowResult] ❌ Prompt non valido, blocco ricerca immediatamente');
            // Pulisci tutto e vai direttamente alla pagina di errore
            setBridgeMessage(''); // NON mostrare nessun messaggio bridge
            setCurrentPrompt(''); // Pulisci anche il prompt per non mostrare il loading
            setSearchMessage(bridgeResult.message || t('freeTextSearch.invalidRequest'));
            setItems([]);
            setFilteredItems([]);
            setLoading(false);
            setIsSearching(false);
            wakeLockService.release(); // Rilascia wake lock
            return; // BLOCCA - non chiamare Gemini 2.5
        }
        
        // STEP 2: Bridge valido - mostra messaggio e parte ricerca principale
        // Il loader è già visibile (settato al click), ora aggiungiamo il bridge message
        // Solo se il bridge ha restituito una stringa valida (non JSON)
        // Verifica anche che non contenga JSON
        if (bridgeResult && typeof bridgeResult === 'string') {
            const cleanResult = bridgeResult.trim();
            // Se inizia con {, potrebbe essere JSON - non mostrarlo
            if (!cleanResult.startsWith('{') && cleanResult && bridgeRequestIdRef.current === currentBridgeRequestId) {
                console.log('[Bridge] 📝 Setting bridge message:', cleanResult);
                setBridgeMessage(cleanResult);
                // Il loader è già visibile, ora mostra il bridge message con animazione dattilografia
            } else {
                setBridgeMessage(''); // Pulisci se contiene JSON
            }
        }
        
        try {
            const searchStart = performance.now();
            setCurrentSearchQuery(trimmedText);
            
            // Chiama Gemini con la nuova funzione (passa la lingua per generare risposte nella lingua corretta)
            const result = await getFreeTextSearchFromGemini(trimmedText, searchType, language);
            const searchElapsed = performance.now() - searchStart;
            console.log(`[Performance] Gemini search completed in ${searchElapsed.toFixed(0)} ms`);
            
            if (!result) {
                setSearchMessage(getServerErrorMessage(language));
                setError(null);
                setLoading(false);
                setIsSearching(false);
                setCurrentType(null);
                await wakeLockService.release();
                return;
            }
            
            // Gestisci risposta negativa
            if (result.valid === false) {
                setSearchMessage(result.message || t('freeTextSearch.invalidRequest'));
                setItems([]);
                setFilteredItems([]);
                setLoading(false);
                setIsSearching(false);
                setCurrentType(null);
                await wakeLockService.release();
                return;
            }
            
            // Risposta positiva - ci sono risultati
            if (result.valid === true && result.items && result.items.length > 0) {
                console.log('[ShowResult] ✅ Gemini responded with', result.items.length, 'items');
                console.log('[ShowResult] 🔄 Setting items, currentType:', currentType);
                // Assicura che currentType sia 'similar' per far partire il filtering
                if (currentType !== 'similar') {
                    console.log('[ShowResult] ⚠️ currentType is not similar, setting it to similar');
                    setCurrentType('similar');
                }
                setItems(result.items);
                setFreeTextAnalysis({
                    title: result.title || '',
                    analysis: result.analysis || '',
                    language: result.language || language
                });
                // Input resta vuoto: il testo resta solo nel prompt corsivo / currentSearchQuery
                setSearchQuery('');
                setCurrentSearchQuery(trimmedText);
                // IMPORTANTE: Reset hasSearchPersistence per nuova richiesta
                setHasSearchPersistence(false);
                // Marca come nuova richiesta Similar
                isNewSimilarRequestRef.current = true;
                // NON settare activeIndex qui - lo farà l'auto-slide useEffect quando Swiper è pronto
                setActiveIndex(0); // Inizia dalla Hero
                // IMPORTANTE: Mantieni loading=true per mostrare il bridge durante il filtering
                // Il loading verrà spento dal useEffect di filtering quando filteredItems è pronto
                if (!loading) {
                    setLoading(true);
                }
            } else {
                setSearchMessage(t('showResult.noResults'));
                setItems([]);
                setFilteredItems([]);
                setLoading(false);
                setCurrentType(null);
                await wakeLockService.release();
                return;
            }
            
        } catch (error) {
            console.error('Errore ricerca libera:', error);
            setSearchMessage(getServerErrorMessage(language));
            setError(null);
            setItems([]);
            setFilteredItems([]);
            setLoading(false);
            setCurrentType(null);
        } finally {
            setIsSearching(false);
            // 🔓 Rilascia wake lock quando la ricerca è completata
            await wakeLockService.release();
        }
    };

    // Funzione per popolare l'input dall'autocomplete e avviare subito la ricerca (LEGACY - mantenuta per compatibilità)
    const handleAutocompleteSelect = async (selectedName, fullSuggestion) => {
        setIsSearching(true); // Disabilita pulsanti durante ricerca
        setSearchQuery(selectedName);
        setHasSelectedFromDropdown(true);
        setSearchMessage('');
        bridgeRequestIdRef.current += 1;
        setBridgeMessage('');
        
        // 🔒 WAKE LOCK: Mantieni schermo attivo durante ricerca (specialmente su mobile)
        await wakeLockService.request();
        setLoading(true);
        setError(null);
        
        try {
            if (searchType === 'artist') {
                // RICERCA ARTISTI SIMILI
        const spotifyId = fullSuggestion?.spotifyId || null;
                const artistImage = fullSuggestion?.imageUrl || null;
                const genres = fullSuggestion?.genres || null;
        
        debugLog('🎵 [Gemini Input] Artist selected:', {
                    name: selectedName,
            spotifyId: spotifyId,
            genres: genres,
            imageUrl: artistImage ? '✅ Available' : '❌ Missing'
        });
        
                setCurrentSearchQuery(selectedName);
        setCurrentArtistImage(artistImage);
        
                const cacheResult = await ArtistCacheService.getSimilarArtists(selectedName, spotifyId, genres);
        
            const results = cacheResult.artists
                .map(artist => ({
                    name: artist.name,
                    match: (artist.similarity / 100).toString(),
                    url: `https://www.last.fm/music/${encodeURIComponent(artist.name)}`,
                    image: []
                }))
                .filter((artist, index, self) => 
                    index === self.findIndex(a => a.name.toLowerCase() === artist.name.toLowerCase())
                );
            
                debugLog(`[ShowResult] Source: ${cacheResult.source || 'unknown'} for "${selectedName}" (${results.length} unique artists)`);
            
            if (cacheResult.artistInfo) {
                setCurrentArtistInfo(cacheResult.artistInfo);
            } else {
                setCurrentArtistInfo(null);
            }
            
            if (results && results.length > 0) {
                setItems(results);
                setActiveIndex(0);
                navigate('/similar', {
                    state: {
                        items: results,
                        type: 'similar',
                            query: selectedName,
                        spotifyId: spotifyId,
                            artistImage: artistImage,
                            artistInfo: cacheResult.artistInfo || null,
                            searchType: 'artist'
                    },
                    replace: true
                });
            } else {
                setSearchMessage(t('showResult.noSimilarFound'));
                }
            } else {
                // RICERCA TRACCE SIMILI
                const trackName = selectedName;
                const artistName = fullSuggestion?.artistName || 'Unknown Artist';
                const spotifyTrackId = fullSuggestion?.spotifyId || null;
                const trackImage = fullSuggestion?.albumImageUrl || fullSuggestion?.imageUrl || null;
                
        debugLog('🎵 [Gemini Input] Track selected:', {
                    track: trackName,
                    artist: artistName,
                    spotifyTrackId: spotifyTrackId,
                    imageUrl: trackImage ? '✅ Available' : '❌ Missing'
                });
                
                setCurrentSearchQuery(`${trackName} by ${artistName}`);
                setCurrentArtistImage(trackImage);
                
                const tracksResult = await getSimilarTracksFromGemini(trackName, artistName, spotifyTrackId);
                
                // Gestisci risultato (può essere array o oggetto con tracks e trackInfo)
                const tracksArray = Array.isArray(tracksResult) ? tracksResult : (tracksResult?.tracks || []);
                const trackInfo = tracksResult?.trackInfo || null;
                
                if (!tracksArray || tracksArray.length === 0) {
                    setSearchMessage(t('showResult.noSimilarTracksFound'));
                    return;
                }
                
                debugLog(`[ShowResult] Found ${tracksArray.length} similar tracks`);
                
                setItems(tracksArray);
                setActiveIndex(0);
                navigate('/similar', {
                    state: {
                        items: tracksArray,
                        type: 'similar',
                        query: `${trackName} by ${artistName}`,
                        trackName: trackName,
                        artistName: artistName,
                        spotifyTrackId: spotifyTrackId,
                        trackImage: trackImage,
                        trackInfo: trackInfo,
                        searchType: 'track'
                    },
                    replace: true
                });
            }
        } catch (error) {
            setSearchMessage(error.message || 'Search failed. Please try again.');
        } finally {
            setLoading(false);
            setIsSearching(false);
            await wakeLockService.release();
        }
    };



    const handleDiscover = () => {
        // Usa state?.type, currentType, o il pathname come fallback
        let typeToUse = state?.type || currentType;
        
        // Se ancora null, usa il pathname per determinare il tipo
        if (!typeToUse) {
            const path = currentPath;
            if (path === '/random') typeToUse = 'random';
            else if (path === '/similar') typeToUse = 'similar';
            else if (path.includes('top-artists')) typeToUse = 'top-artists';
        }
        
        // Se ancora null, prova a salvarlo da currentType se esiste
        if (!typeToUse && currentType) {
            typeToUse = currentType;
        }
        
        debugLog('🔄 [handleDiscover] Type:', typeToUse, 'Current items:', items.length, 'State:', state?.type, 'CurrentType:', currentType, 'Pathname:', currentPath);
        
        if (!typeToUse) {
            console.error('❌ [handleDiscover] Cannot determine type!');
            return;
        }
        
        // Aggiorna currentType se era null
        if (!currentType && typeToUse) {
            setCurrentType(typeToUse);
        }
        
        if (typeToUse === 'similar') {
            // Per similar, cerca altri artisti simili dello stesso artista (non gli stessi)
            const currentQuery = state?.query || searchQuery;
            if (currentQuery) {
                // Reset e lascia che l'useEffect gestisca la richiesta
                debugLog('🔄 [handleDiscover] Similar - Resetting');
                requestMade.current = false;
                setItems([]); // Trigger useEffect
            }
        } else if (typeToUse === 'random') {
            // Per random, ricarica con nuovi artisti random
            debugLog('🔄 [handleDiscover] Random - Resetting');
            PersistenceService.clearRandomState();
            setHasRandomPersistence(false);
            // Reset e lascia che l'useEffect gestisca la richiesta
            requestMade.current = false;
            setItems([]);
            setFilteredItems([]);
            setActiveIndex(0);
            triggerRandomGeneration();
        } else if (typeToUse === 'top-artists') {
            // Per top-artists, ricarica con nuovi top artists
            debugLog('🔄 [handleDiscover] Top artists - Resetting');
            requestMade.current = false;
            setItems([]); // Trigger useEffect
        }
    };

    const handleBackToSearch = () => {
        // Torna alla hero di similar (slide 0) con la searchbar
        setActiveIndex(0);
        setError(null);
        setSearchMessage('');
        if (swiperInstance) swiperInstance.slideTo(0, 300);
    };

    const handleNewSearch = async () => {
        // Desktop: reset completo e torna alla pagina base ricerca
        try {
            PersistenceService.clearSearchState?.();
        } catch (e) { /* ignore */ }
        setItems([]);
        setFreeTextAnalysis(null);
        setBridgeMessage('');
        setCurrentPrompt('');
        setSearchQuery('');
        setActiveIndex(0);
        setError(null);
        setSearchMessage('');
        navigate('/similar', {
            state: { type: 'similar', forceReset: true, timestamp: Date.now() },
            replace: true
        });
    };

    const handleBackToFirstPage = () => {
        if (currentType === 'daily-discovery-artists') {
            isNavigatingBackToDailyRef.current = true;
            PersistenceService.clearDailyState();
            navigate('/daily-discovery');
        } else if (currentType === 'top-artists') {
            navigate('/home');
        } else if (currentType === 'random') {
            setActiveIndex(0);
            if (swiperInstance) swiperInstance.slideTo(0, 300);
        }
    };

    const handleHome = () => {
        // Se siamo in daily-discovery con schede, torna alla pagina testuale
        if (state?.type === 'daily-discovery') {
            navigate('/daily-discovery');
        } else {
            // Torna sempre alla home
            navigate('/home');
        }
    };

    // Handler per navigazione con salvataggio posizione
    const handleNavigateWithState = (path, newState = {}) => {
        // Per random, NON salvare items (deve sempre generare nuovi artisti)
        const stateToSave = currentType === 'random' 
            ? { ...newState, savedIndex: activeIndex } // NO items per random
            : { ...newState, savedIndex: activeIndex, items: items }; // Salva items per altri tipi
        
        navigate(path, { 
            state: stateToSave
        });
    };

    // Funzione per ottenere il nome del paese per display
    const getCountryDisplayName = (countryCode) => {
        const countryNames = {
            'italy': 'Italian',
            'united states': 'US',
            'spain': 'Spanish', 
            'france': 'French',
            'germany': 'German',
            'portugal': 'Portuguese',
            'netherlands': 'Dutch',
            'poland': 'Polish',
            'russia': 'Russian',
            'japan': 'Japanese',
            'south korea': 'Korean',
            'china': 'Chinese',
            'saudi arabia': 'Saudi',
            'turkey': 'Turkish',
            'sweden': 'Swedish',
            'norway': 'Norwegian',
            'denmark': 'Danish',
            'finland': 'Finnish',
            'iceland': 'Icelandic',
            'united kingdom': 'UK',
            'canada': 'Canadian',
            'australia': 'Australian',
            'brazil': 'Brazilian',
            'mexico': 'Mexican',
            'argentina': 'Argentinian',
            'india': 'Indian'
        };
        
        return countryNames[countryCode] || countryCode || 'Country';
    };

    // Funzione per ottenere il testo e l'icona del pulsante discover
    const getDiscoverButtonProps = () => {
        const currentType = state?.type;
        
        if (currentType === 'top-artists') {
            if (currentCountry === 'global') {
                // Usa il paese reale rilevato (quello salvato in currentCountry all'inizio)
                const savedCountry = localStorage.getItem('detectedCountry');
                const countryName = getCountryDisplayName(savedCountry || 'italy');
                return {
                    text: `${countryName} Chart`,
                    icon: "bi bi-flag"
                };
            } else {
                return {
                    text: "Global Chart", 
                    icon: "bi bi-globe"
                };
            }
        } else if (currentType === 'similar') {
            return {
                text: "Show More",
                icon: "bi bi-arrow-repeat"
            };
        } else if (currentType === 'random') {
            return {
                text: t('showResult.discoverMore'),
                icon: "bi bi-arrow-repeat"
            };
        }
        
        return {
            text: t('showResult.discoverMore'),
            icon: "bi bi-arrow-repeat"
        };
    };

    const handleToggleChart = async () => {
        try {
            setLoading(true);
            setError(null);
            
            if (currentCountry === 'global') {
                // Torna alla chart del paese
                let detectedCountry;
                try {
                    detectedCountry = await getCountryFromIP();
                } catch (error) {
                    console.error('Errore rilevamento IP in toggle:', error);
                    detectedCountry = getCountryFromLanguage();
                }
                setCurrentCountry(detectedCountry);
                
                const countryResults = await getTopArtists(20, detectedCountry);
                const countryResultsWithRank = countryResults.map((artist, index) => ({
                    ...artist,
                    rank: index + 1
                }));
                
                setItems(countryResultsWithRank);
            } else {
                // Vai alla chart globale
                setCurrentCountry('global');
                
                const globalResults = await getTopArtists(20, 'global');
                const globalResultsWithRank = globalResults.map((artist, index) => ({
                    ...artist,
                    rank: index + 1
                }));
                
                setItems(globalResultsWithRank);
            }
        } catch (error) {
            // Handle chart loading errors silently
            console.error('[handleToggleChart] Error:', error);
            setSearchMessage(getServerErrorMessage(language));
            setError(null);
            setCurrentType(null);
        } finally {
            setLoading(false);
        }
    };

    // EARLY RETURN: Se non siamo su una route che richiede ShowResult, non renderizzare nulla
    // DEVE essere dopo tutti i hook React
    const showResultRoutes = ['/similar', '/random', '/daily-discovery-artists', '/top-artists'];
    const isShowResultRoute = showResultRoutes.some(route => currentPath === route || currentPath.startsWith(route));
    
    if (!isShowResultRoute && !state?.type && !currentType) {
        return null; // Non renderizzare se non siamo su una route ShowResult
    }

    // Mostra loader se: stiamo caricando o stiamo filtrando i risultati
    // Il loader deve rimanere fino a quando filteredItems è popolato
    const isFiltering = items.length > 0 && filteredItems.length === 0 && !error && !searchMessage;
    const isRandomWaitingForItems = (currentType === 'random' || pathname === '/random') && items.length > 0 && filteredItems.length === 0;
    
    // Per similar: aspetta che Swiper sia pronto SOLO se filteredItems è già popolato (per evitare pagina nera)
    // Se filteredItems è vuoto, non aspettare Swiper (il filtering è ancora in corso)
    const shouldWaitForSwiper = currentType === 'similar' && filteredItems.length > 0 && !swiperReady && !error && !searchMessage && !isFilteringArtists;
    
    // Per similar: mostra loader se stiamo caricando, filtrando, aspettando Swiper, O se lo slide non è completato
    const isSimilar = currentType === 'similar' || pathname === '/similar';
    const shouldShowSimilarLoader = isSimilar && (
        loading || 
        isFilteringArtists || 
        isFiltering || 
        shouldWaitForSwiper || 
        !similarSlideCompletedRef.current
    );
    
    // Per altri tipi: mostra loader se stiamo caricando, filtrando, o aspettando
    const shouldShowLoader = shouldShowSimilarLoader || loading || isFilteringArtists || isFiltering || isRandomWaitingForItems || shouldWaitForSwiper;
    
    // Log quando il loader viene mostrato/nascosto (solo per random)
    if (currentType === 'random' || pathname === '/random') {
        console.log('👁️ [RENDER] shouldShowLoader:', shouldShowLoader, {
            loading,
            isFilteringArtists,
            isFiltering,
            isRandomWaitingForItems,
            shouldWaitForSwiper,
            itemsLength: items.length,
            filteredItemsLength: filteredItems.length,
            swiperReady,
            firstSlideReady
        });
    }
    
    // Per random/similar senza items (solo hero slide), nascondi il loader quando Swiper è pronto
    const isHeroOnlyMode = (isRandomExperience || isSearchExperience) && filteredItems.length === 0 && !loading && !isFiltering;
    const isRandom = currentType === 'random' || pathname === '/random';
    
    // SIMILAR: overlay loader (non early return). Random: resta sulla hero, niente blank screen.
    const isRestoringPosition = isSimilar && hasSearchPersistence && activeIndex > 0 && (!swiperReady || (swiperInstance && swiperInstance.activeIndex !== activeIndex));
    const shouldShowSimilarLoaderOverlay = !isDesktopLayout && ((isSimilar && shouldShowSimilarLoader && !hasSearchPersistence) || isRestoringPosition);
    const displayPrompt = currentPrompt || currentSearchQuery || searchQuery;
    const displayBridgeMessage = bridgeMessage || '';
        
    // Mostra loader solo se non siamo in hero-only mode E non siamo su random/similar
    // OTTIMIZZAZIONE: Se abbiamo persistenza attiva e dati, NON mostrare il loader
    const hasAnyPersistence = hasRandomPersistence || hasSearchPersistence || hasDailyPersistence;
    if (shouldShowLoader && !isHeroOnlyMode && !isRandom && !isSimilar && !hasAnyPersistence) {
        // Per charts usa il loading normale (skeleton)
        return <LoadingSlides />;
    }

    if (error || searchMessage) {
        // Estrai solo il messaggio se è un oggetto JSON
        let displayMessage = searchMessage || error;
        if (typeof displayMessage === 'object' && displayMessage.message) {
            displayMessage = displayMessage.message;
        }
        // Se è una stringa che inizia con {, prova a parsare
        if (typeof displayMessage === 'string' && displayMessage.trim().startsWith('{')) {
            try {
                const parsed = JSON.parse(displayMessage);
                if (parsed.message) {
                    displayMessage = parsed.message;
                }
            } catch (e) {
                // Non è JSON valido, usa come è
            }
        }
        
        return (
            <div className="recommend-container search-page-container">
                <div className="artist-slide">
                    <div className="artist-content">
                        {/* Cerchio decorativo con immagine love.jpg */}
                        <div className="decorative-circle" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '40px',
                            marginTop: '20px'
                        }}>
                            <div style={{
                                width: '200px',
                                height: '200px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                            }}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/img/love.jpg`}
                                    alt="Error"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </div>
                        </div>
                        
                        <h2 className="artist-name">
                            {t('showResult.noResults')}
                        </h2>
                    
                        <p className="artist-genres" style={{
                            fontSize: '16px',
                            textAlign: 'center',
                            marginBottom: '30px',
                            lineHeight: '1.5',
                            maxWidth: '400px',
                            margin: '0 auto 30px auto',
                            padding: '0 20px'
                        }}>
                            {displayMessage}
                        </p>
                        
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '15px',
                            alignItems: 'center',
                            marginTop: '30px'
                        }}>
                            {/* Pulsante "Scopri altro" - cambia in base al tipo corrente */}
                            <button
                                onClick={async () => {
                                    await resetSearchState();
                                    if (currentType === 'random') {
                                        navigate('/random', { state: { type: 'random' } });
                                    } else if (currentType === 'similar') {
                                        navigate('/similar', { state: { type: 'similar' } });
                                    } else {
                                        navigate('/');
                                    }
                                }}
                                className="discover-button"
                            >
                                <i className="bi bi-arrow-repeat" />
                                {t('showResult.discoverMore')}
                            </button>
                            <button
                                onClick={async () => {
                                    await resetSearchState();
                                    handleHome();
                                }}
                                className="home-button"
                            >
                                <i className="bi bi-house" />
                                {t('showResult.backToHome')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    
    // Calcola il numero totale di slide (hero + artisti + action slide)
    const heroSlidesCount = (isRandomExperience || isSearchExperience || isDailyExperience) ? 1 : 0;
    const hasActionSlide = filteredItems.length > 0;
    const actionSlidesCount = hasActionSlide ? 1 : 0;
    const computedSlides = heroSlidesCount + filteredItems.length + actionSlidesCount;
    const totalSlides = computedSlides > 0 ? computedSlides : 1;
    
    // Assicurati che activeIndex non sia maggiore di totalSlides (evita di andare all'ultima slide)
    // Per daily senza persistenza: inizia su slide 1 (primo artista), non sulla hero slide 0
    const safeActiveIndex = (() => {
        const base = Math.min(activeIndex, Math.max(0, totalSlides - 1));
        if (isDailyExperience && base === 0 && !hasDailyPersistence && filteredItems.length > 0) {
            return Math.min(1, totalSlides - 1);
        }
        return base;
    })();

    // Handler per il click sui pallini
    const handleBulletClick = (slideIndex) => {
        if (swiperInstance) {
            swiperInstance.slideTo(slideIndex);
        }
    };

    // Determina quali slide devono caricare i dati
    // Carica solo: slide attiva + 5-10 slide in avanti (non indietro per evitare troppe chiamate)
    // Precarica album per slide attiva + 2 avanti (staggered nel useEffect di ArtistSlide)
    const shouldPreloadAlbumsSlide = (slideIndex) =>
        slideIndex >= safeActiveIndex && slideIndex <= safeActiveIndex + 3;

    const shouldLoadSlide = (slideIndex) => {
        // Carica sempre la slide attiva
        if (slideIndex === activeIndex) {
            return true;
        }
        
        // Carica solo le slide in avanti (dopo quella attiva)
        // Range: 5-10 slide in avanti (non indietro)
        const PRELOAD_AHEAD = 7; // 7 slide in avanti (compresa quella attiva = 8 totali)
        const PRELOAD_BEHIND = 2; // Solo 2 slide indietro (per scroll veloce all'indietro)
        
        // Se siamo all'inizio, carica le prime 5-7 slide
        if (activeIndex <= 1 && slideIndex < 7) {
            return true;
        }
        
        // Carica slide in avanti (slideIndex > activeIndex)
        if (slideIndex > activeIndex && slideIndex <= activeIndex + PRELOAD_AHEAD) {
            return true;
        }
        
        // Carica solo 2 slide indietro (per scroll veloce all'indietro)
        if (slideIndex < activeIndex && slideIndex >= activeIndex - PRELOAD_BEHIND) {
            return true;
        }
        
        return false;
    };

    return (
        <div className={`recommend-container ${(isSearchExperience || isRandomExperience) ? 'search-page-container' : ''} ${isDesktopLayout && filteredItems.length > 0 ? 'is-desktop-grid' : ''}`}>
            {/* Avatar analisi: solo mobile — sotto i tasti azione (share/star/more) */}
            {currentType === 'similar' && freeTextAnalysis && activeIndex !== 0 && !isDesktopLayout && (
                <div
                    className="search-analysis-fab"
                    style={{
                    position: 'absolute',
                    top: '20px',
                    right: '16px',
                    zIndex: 900
                }}>
                    <div
                        onClick={() => setShowAnalysisModal(true)}
                        style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                            transition: 'all 0.2s',
                            border: '2px solid rgba(255, 255, 255, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                        }}
                        title={language === 'it' ? 'Leggi analisi' : 'Read analysis'}
                    >
                        <img 
                            src={`${process.env.PUBLIC_URL}/img/ok.jpg`} 
                            alt="Analysis" 
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Artist Avatar - solo mobile legacy */}
            {currentType === 'similar' && !freeTextAnalysis && state?.searchType !== 'track' && state?.query && state?.artistImage && activeIndex !== 0 && !isDesktopLayout && (
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '16px',
                    zIndex: 900
                }}>
                    <ArtistAvatar
                        artistName={state.query}
                        artistImage={state.artistImage}
                        onClick={() => setShowArtistInfoModal(true)}
                    />
                </div>
            )}

            {/* Track Avatar - solo mobile legacy */}
            {currentType === 'similar' && !freeTextAnalysis && state?.searchType === 'track' && state?.trackImage && activeIndex !== 0 && !isDesktopLayout && (
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '16px',
                    zIndex: 900
                }}>
                    <ArtistAvatar
                        artistName={state?.query || `${state?.trackName} by ${state?.artistName}`}
                        artistImage={state.trackImage}
                        onClick={() => setShowArtistInfoModal(true)}
                    />
                </div>
            )}

            {/* Artist/Track Info Modal */}
            {showArtistInfoModal && (
                <ArtistInfoModal
                    artistName={state?.searchType === 'track' 
                        ? (state?.query || `${state?.trackName} by ${state?.artistName}`)
                        : state?.query}
                    artistImage={state?.searchType === 'track' 
                        ? state?.trackImage 
                        : state?.artistImage}
                    spotifyId={state?.searchType === 'track' 
                        ? state?.spotifyTrackId 
                        : state?.spotifyId}
                    artistInfoFromAI={state?.searchType === 'track' ? null : (state?.artistInfo || currentArtistInfo)}
                    trackInfoFromAI={state?.searchType === 'track' ? (state?.trackInfo || null) : null}
                    isTrack={state?.searchType === 'track'}
                    onClose={() => setShowArtistInfoModal(false)}
                />
            )}

            {/* Modal Analisi Ricerca Libera (Foundly 2.0) */}
            {showAnalysisModal && freeTextAnalysis && (
                <div
                    onClick={() => setShowAnalysisModal(false)}
                    style={{
                        position: 'fixed', inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.65)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        animation: 'fadeIn 0.2s ease-out',
                    }}
                >
                    <div 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#1a1a1a',
                            borderRadius: '20px',
                            padding: '24px 20px 32px',
                            maxWidth: '480px',
                            width: '100%',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            position: 'relative',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                            animation: 'slideUp 0.3s ease-out',
                        }}
                    >
                        {/* Close button */}
                        <button 
                            onClick={() => setShowAnalysisModal(false)} 
                            aria-label="Close"
                            style={{
                                position: 'absolute',
                                top: '14px',
                                right: '14px',
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                backgroundColor: 'rgba(255,255,255,0.06)',
                                color: 'white',
                                fontSize: '16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 1,
                            }}
                        >
                            ✕
                        </button>

                        {/* Immagine ok.jpg */}
                        <div className="artist-info-modal-image-container">
                            <img 
                                src={`${process.env.PUBLIC_URL}/img/ok.jpg`} 
                                alt="Analysis"
                                className="artist-info-modal-image"
                            />
                        </div>

                        {/* Titolo (max 10 parole, font più piccolo ma bold) */}
                        <h2 className="artist-info-modal-name" style={{
                            fontSize: '20px',
                            fontWeight: '700'
                        }}>
                            {freeTextAnalysis.title || ''}
                        </h2>

                        {/* Analisi nella lingua corretta */}
                        <div className="artist-info-modal-description">
                            <p>
                                {freeTextAnalysis.analysis || ''}
                            </p>
                        </div>
                    </div>
                </div>
            )}


            {isDesktopLayout && filteredItems.length > 0 && (
                <div className="desktop-results">
                    <PageHeader
                        title={
                            currentType === 'random'
                                ? (language === 'it' ? 'Casuali' : 'Random')
                                : currentType === 'daily-discovery-artists'
                                    ? (state?.query || (language === 'it' ? 'Approfondimento' : 'Discovery'))
                                    : currentType === 'similar'
                                        ? t('freeTextSearch.title')
                                        : (language === 'it' ? 'Risultati' : 'Results')
                        }
                        subtitle={
                            currentType === 'random'
                                ? t('randomHero.subtitle')
                                : currentType === 'daily-discovery-artists'
                                    ? (language === 'it'
                                        ? 'Gli artisti di questo approfondimento'
                                        : 'Artists from this discovery')
                                    : currentType === 'similar'
                                        ? t('freeTextSearch.description')
                                        : null
                        }
                    />
                    {currentType === 'similar' && (
                        <div className="desktop-results-search">
                            <FreeTextInput
                                value={searchQuery}
                                onChange={(value) => {
                                    setSearchQuery(value);
                                    setSearchMessage('');
                                }}
                                onSubmit={handleFreeTextSubmit}
                                placeholder={language === 'it' ? "Puoi scrivere o parlare nella tua lingua" : "You can write or speak in your language"}
                                className="search-hero-input search-hero-input--compact"
                                disabled={isSearching}
                            />
                        </div>
                    )}
                    {currentType === 'similar' && freeTextAnalysis && (
                        <div className="desktop-results-analysis">
                            {freeTextAnalysis?.title && (
                                <h3 className="desktop-results-analysis__title">{freeTextAnalysis.title}</h3>
                            )}
                            {freeTextAnalysis?.analysis && (
                                <p className="desktop-results-analysis__text">
                                    {freeTextAnalysis.analysis}
                                </p>
                            )}
                        </div>
                    )}
                    <div className="desktop-results-grid">
                        {filteredItems.map((artist, index) => (
                            <ArtistCard
                                key={`${artist.name || 'artist'}-${artist.id || index}`}
                                variant="grid"
                                artist={artist}
                                onOpenListenModal={(artistName, albumName, ids, mode) =>
                                    setListenModal({
                                        isOpen: true,
                                        artistName,
                                        albumName,
                                        ids: ids || {},
                                        mode: mode || 'listen'
                                    })
                                }
                            />
                        ))}
                    </div>
                    <div className="desktop-results-actions">
                        {currentType === 'random' && (
                            <button type="button" onClick={handleDiscover} aria-label={t('showResult.discoverMore')}>
                                <span>{t('showResult.discoverMore')}</span>
                            </button>
                        )}
                        {(currentType === 'top-artists' || currentType === 'daily-discovery-artists') && (
                            <button type="button" onClick={handleDiscover} aria-label={getDiscoverButtonProps().text}>
                                <span>{getDiscoverButtonProps().text}</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {!(isDesktopLayout && filteredItems.length > 0) && (
            <>
            <Swiper
                key={`${currentType}-${initialData.items.length}`} // KEY DINAMICA: Forza ricreazione se cambia tipo o dati iniziali
                spaceBetween={0}
                slidesPerView={1}
                initialSlide={safeActiveIndex}
                onSwiper={(swiper) => {
                    const timestamp = performance.now();
                    console.log('🎯 [onSwiper] ===== SWIPER INITIALIZED =====');
                    console.log('[ShowResult] 🎯 Swiper initialized:', {
                        initialized: swiper?.initialized,
                        slides: swiper?.slides?.length,
                        filteredItemsLength: filteredItems.length,
                        timestamp: `${timestamp.toFixed(0)}ms`
                    });
                    setSwiperInstance(swiper);
                    // Marca Swiper come pronto quando è inizializzato
                    // SEMPRE, anche se mostra solo la hero slide
                    if (swiper && swiper.initialized) {
                        console.log('🎯 [onSwiper] ✅ Setting swiperReady = TRUE');
                        setSwiperReady(true);
                    } else {
                        console.log('🎯 [onSwiper] ⚠️ Swiper not fully initialized yet');
                    }
                }}
                onSlideChange={(swiper) => {
                    const newIndex = swiper.activeIndex;
                    console.log('[Persistence] 🔄 onSlideChange called:', {
                        newIndex,
                        currentType,
                        hasRandomPersistence,
                        hasSearchPersistence,
                        hasDailyPersistence,
                        filteredItemsLength: filteredItems.length,
                        swiperInitialized: swiper.initialized,
                        swiperDestroyed: swiper.destroyed
                    });
                    // Per daily: slide 0 = torna alla pagina daily (naviga indietro)
                    if (newIndex === 0 && isDailyExperience) {
                        navigate(-1);
                        return;
                    }
                    setActiveIndex(newIndex);
                    if (newIndex === 0 && (isRandomExperience || isSearchExperience)) {
                        setHeroSuggestionSeed(Date.now());
                    }
                    // Per similar: marca lo slide come completato quando arriva a slide 1
                    if (currentType === 'similar' && newIndex === 1) {
                        console.log('[Similar Loader] ✅ Slide 1 reached, marking slide as completed');
                        similarSlideCompletedRef.current = true;
                    }
                    
                    // Salva per Random
                    // IMPORTANTE: Salva activeIndex SEMPRE quando ci sono filteredItems, anche se hasRandomPersistence è false
                    // (perché potrebbe essere una nuova richiesta in corso, ma vogliamo salvare la posizione)
                    if (currentType === 'random' && filteredItems.length > 0 && swiper.initialized && !swiper.destroyed) {
                        console.log('[Random Persistence] 💾 Saving activeIndex to persistence:', newIndex);
                        // Leggi lo stato corrente per fare merge (potrebbe esserci già items salvato)
                        const currentState = PersistenceService.getRandomState();
                        PersistenceService.setRandomState({
                            ...currentState,
                            activeIndex: newIndex
                        });
                    }
                    // Salva per Search
                    // IMPORTANTE: Salva activeIndex SEMPRE quando ci sono filteredItems e il filtering è completato
                    // (non richiedere hasSearchPersistence perché potrebbe non essere ancora settato)
                    else if (currentType === 'similar' && filteredItems.length > 0 && !isFilteringArtists && swiper.initialized && !swiper.destroyed) {
                        console.log('[Search Persistence] 💾 Saving activeIndex to persistence:', newIndex);
                        // IMPORTANTE: Leggi lo stato corrente e fai merge per non perdere altri dati
                        const currentState = PersistenceService.getSearchState();
                        PersistenceService.setSearchState({
                            ...currentState,
                            activeIndex: newIndex
                        });
                    }
                    // Salva per Daily (posizione + discoveryId per ripristino dal nav)
                    else if ((currentType === 'daily-discovery-artists' || currentType === 'top-artists') && filteredItems.length > 0 && swiper.initialized && !swiper.destroyed) {
                        const toSave = { activeIndex: newIndex };
                        if (state?.discoveryId) toSave.discoveryId = state.discoveryId;
                        if (state?.discoveryData) toSave.discoveryData = state.discoveryData;
                        if (state?.query) toSave.query = state.query;
                        if (state?.items?.length) toSave.items = state.items; // per ripristino diretto senza refetch
                        console.log('[Daily Persistence] 💾 Saving to persistence:', toSave.activeIndex, toSave.discoveryId ? 'with discoveryId' : '');
                        PersistenceService.setDailyState(toSave);
                        setHasDailyPersistence(true);
                    }
                    else {
                        console.log('[Persistence] ⏭️ Skipping save, conditions not met');
                    }
                }}
                className="recommend-swiper"
            >
                {isRandomExperience && (
                    <SwiperSlide key="random-hero">
                        <div className="artist-slide">
                            <div className="artist-content">
                                <div className="decorative-circle" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '40px',
                                    marginTop: '20px'
                                }}>
                                    <div style={{
                                        width: '220px',
                                        height: '220px',
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        boxShadow: '0 6px 30px rgba(0,0,0,0.55)'
                                    }}>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/img/random.jpg`}
                                            alt={t('home.randomArtists.title')}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </div>
                                </div>

                                <h2 className="artist-name">
                                    {t('home.randomArtists.title')}
                                </h2>
                                <p className="artist-genres" style={{
                                    maxWidth: '320px',
                                    margin: '0 auto 15px',
                                    color: 'rgba(255,255,255,0.7)'
                                }}>
                                    {t('randomHero.subtitle')}
                                </p>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginTop: '40px',
                                    paddingBottom: '40px'
                                }}>
                                    <button
                                        onClick={triggerRandomGeneration}
                                        disabled={loading || isGeneratingRandom}
                                        style={{
                                            padding: '15px 40px',
                                            backgroundColor: 'white',
                                            color: '#121212',
                                            border: 'none',
                                            borderRadius: '30px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            cursor: (loading || isGeneratingRandom) ? 'not-allowed' : 'pointer',
                                            boxShadow: '0 4px 20px rgba(255,255,255,0.2)',
                                            transition: 'all 0.3s ease',
                                            opacity: (loading || isGeneratingRandom) ? 0.6 : 1
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!loading && !isGeneratingRandom) {
                                                e.target.style.transform = 'scale(1.05)';
                                                e.target.style.boxShadow = '0 6px 25px rgba(255,255,255,0.3)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'scale(1)';
                                            e.target.style.boxShadow = '0 4px 20px rgba(255,255,255,0.2)';
                                        }}
                                    >
                                        {isGeneratingRandom
                                            ? (language === 'it' ? 'Sto cercando…' : 'Searching…')
                                            : t('randomHero.cta')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                )}
                {isSearchExperience && (
                    <SwiperSlide key="search-hero">
                    {isDesktopLayout ? (
                        <div className={`artist-slide search-hero-slide ${isInputFocused ? 'input-focused' : ''} ${loading || isSearching ? 'is-searching' : ''}`}>
                            <div className={`artist-content search-hero-content ${isInputFocused ? 'input-focused' : ''}`}>
                                <div className="search-hero-shell">
                                    <PageHeader
                                        className="search-hero-header"
                                        title={t('freeTextSearch.title')}
                                        subtitle={t('freeTextSearch.description')}
                                    />

                                    <div className="search-hero-row search-hero-row--input-only">
                                        <div className="search-hero-copy">
                                            <div
                                                ref={searchWrapperRef}
                                                className="search-input-wrapper"
                                            >
                                                <FreeTextInput
                                                    value={searchQuery}
                                                    onChange={(value) => {
                                                        setSearchQuery(value);
                                                        setSearchMessage('');
                                                    }}
                                                    onSubmit={handleFreeTextSubmit}
                                                    onFocus={() => setIsInputFocused(true)}
                                                    onBlur={() => {
                                                        setTimeout(() => {
                                                            const activeElement = document.activeElement;
                                                            const suggestionsContainer = document.querySelector('.prompt-suggestions-container');
                                                            const isClickingSuggestions = suggestionsContainer &&
                                                                suggestionsContainer.contains(activeElement);
                                                            const isInsideWrapper = searchWrapperRef.current &&
                                                                searchWrapperRef.current.contains(activeElement);
                                                            if (isInputFocused && (isInsideWrapper || isClickingSuggestions)) {
                                                                const input = document.querySelector('.search-hero-input');
                                                                if (input && document.activeElement !== input) {
                                                                    input.focus();
                                                                }
                                                                return;
                                                            }
                                                            if (!isInputFocused && isClickingSuggestions) {
                                                                return;
                                                            }
                                                            if (!isInsideWrapper && !isClickingSuggestions) {
                                                                setIsInputFocused(false);
                                                            }
                                                        }, 200);
                                                    }}
                                                    placeholder={language === 'it' ? "Puoi scrivere o parlare nella tua lingua" : "You can write or speak in your language"}
                                                    className={`search-hero-input ${(loading || isSearching) ? 'search-hero-input--compact' : ''}`}
                                                    disabled={isSearching}
                                                />

                                                {!loading && !isSearching && (
                                                    <PromptSuggestions
                                                        inputWidth={searchWrapperRef.current?.offsetWidth}
                                                        refreshKey={heroSuggestionSeed}
                                                    />
                                                )}

                                                {searchMessage && (
                                                    <div className="search-hero-message">
                                                        {searchMessage}
                                                    </div>
                                                )}
                                            </div>

                                            {(loading || isSearching) && isDesktopLayout && currentPrompt && (
                                                <div className="search-hero-bridge">
                                                    <p className="search-hero-bridge__prompt">{currentPrompt}</p>
                                                    {!displayBridgeMessage ? (
                                                        <p className="search-hero-bridge__thinking">
                                                            {language === 'it' ? 'Sto pensando' : 'Thinking'}…
                                                        </p>
                                                    ) : (
                                                        <TypedBridgeText
                                                            text={displayBridgeMessage}
                                                            className="search-hero-bridge__text"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={`artist-slide ${isInputFocused ? 'input-focused' : ''}`}>
                            <div className={`artist-content ${isInputFocused ? 'input-focused' : ''}`}>
                                <div className="decorative-circle" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '40px',
                                    marginTop: '20px'
                                }}>
                                    <div style={{
                                        width: '200px',
                                        height: '200px',
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                                    }}>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/img/cerca.jpg`}
                                            alt="Discover music"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </div>
                                </div>

                                <h2 className="artist-name">
                                    {t('freeTextSearch.title')}
                                </h2>
                                <p className="artist-genres">
                                    {t('freeTextSearch.description')}
                                </p>

                                <div
                                    ref={searchWrapperRef}
                                    className="search-input-wrapper"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '15px',
                                        marginTop: '30px',
                                        width: '90%',
                                        maxWidth: '90%',
                                        margin: '30px auto 0'
                                    }}
                                >
                                    <FreeTextInput
                                        value={searchQuery}
                                        onChange={(value) => {
                                            setSearchQuery(value);
                                            setSearchMessage('');
                                        }}
                                        onSubmit={handleFreeTextSubmit}
                                        onFocus={() => setIsInputFocused(true)}
                                        onBlur={() => {
                                            setTimeout(() => {
                                                const activeElement = document.activeElement;
                                                const suggestionsContainer = document.querySelector('.prompt-suggestions-container');
                                                const isClickingSuggestions = suggestionsContainer &&
                                                    suggestionsContainer.contains(activeElement);
                                                const isInsideWrapper = searchWrapperRef.current &&
                                                    searchWrapperRef.current.contains(activeElement);
                                                if (isInputFocused && (isInsideWrapper || isClickingSuggestions)) {
                                                    const input = document.querySelector('textarea[placeholder*="Puoi scrivere"], textarea[placeholder*="You can write"]');
                                                    if (input && document.activeElement !== input) {
                                                        input.focus();
                                                    }
                                                    return;
                                                }
                                                if (!isInputFocused && isClickingSuggestions) {
                                                    return;
                                                }
                                                if (!isInsideWrapper && !isClickingSuggestions) {
                                                    setIsInputFocused(false);
                                                }
                                            }, 200);
                                        }}
                                        placeholder={language === 'it' ? "Puoi scrivere o parlare nella tua lingua" : "You can write or speak in your language"}
                                        style={{
                                            padding: '15px',
                                            borderRadius: '25px',
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            fontSize: '14px',
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            color: 'white',
                                            backdropFilter: 'blur(10px)',
                                            width: '100%',
                                            outline: 'none'
                                        }}
                                        disabled={isSearching}
                                    />

                                    <PromptSuggestions
                                        inputWidth={searchWrapperRef.current?.offsetWidth}
                                        refreshKey={heroSuggestionSeed}
                                    />

                                    {searchMessage && (
                                        <div style={{
                                            color: 'white',
                                            fontSize: '14px',
                                            textAlign: 'center',
                                            marginTop: '15px',
                                            padding: '12px 16px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            backdropFilter: 'blur(10px)',
                                            lineHeight: '1.4'
                                        }}>
                                            {searchMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    </SwiperSlide>
                )}


                {/* Hero slide daily: placeholder invisibile — swipe back qui → navigate(-1) verso daily */}
                {isDailyExperience && (
                    <SwiperSlide key="daily-hero">
                        <div style={{ width: '100%', height: '100%', background: '#121212' }} />
                    </SwiperSlide>
                )}

                {filteredItems.length > 0 && filteredItems
                    .filter(item => item && item.name) // Filtra items null o senza nome
                    .map((item, index) => {
                    const currentQuery = currentPrompt || currentSearchQuery || state?.query || '';
                    const currentSearchType = 'artist'; // Sempre artisti
                    const slideIndex = heroSlidesCount + index;
                    return (
                    <SwiperSlide key={`${index}-${item.name || item.id}`}>
                        <ArtistSlide 
                            artist={item}
                            isActive={slideIndex === safeActiveIndex}
                            shouldLoad={shouldLoadSlide(slideIndex)}
                            shouldPreloadAlbums={shouldPreloadAlbumsSlide(slideIndex)}
                            pageType={currentType}
                            position={index}
                            onBackToSearch={currentType === 'similar' ? handleBackToSearch : undefined}
                            onBackToFirstPage={(currentType === 'daily-discovery-artists' || currentType === 'random' || currentType === 'top-artists') ? handleBackToFirstPage : undefined}
                            query={currentQuery}
                            searchType={currentSearchType}
                            onSlideReady={handleSlideReady}
                            onOpenListenModal={(artistName, albumName, ids, mode) => setListenModal({ isOpen: true, artistName, albumName, ids: ids || {}, mode: mode || 'listen' })}
                        />
                    </SwiperSlide>
                    );
                })}

                {/* ActionSlide: mostra SOLO se ci sono items (altrimenti si vede durante il caricamento) */}
                {filteredItems.length > 0 && (
                    <SwiperSlide key="action-slide">
                        <ActionSlide 
                            onHome={handleHome}
                            onDiscover={handleDiscover}
                            onToggleChart={state?.type === 'top-artists' ? handleToggleChart : null}
                            discoverButtonText={getDiscoverButtonProps().text}
                            discoverIcon={getDiscoverButtonProps().icon}
                            showDiscoverButton={state?.type !== 'similar'}
                            pageType={currentType || state?.type}
                            onBackToDiscovery={() => navigate('/daily-discovery')}
                            onBackToSearch={handleBackToSearch}
                            onNewSearch={handleNewSearch}
                            language={language}
                            t={t}
                        />
                    </SwiperSlide>
                )}
            </Swiper>

            {/* Custom Pagination */}
            {totalSlides > 1 && (
                <CustomPagination 
                    totalSlides={totalSlides}
                    activeIndex={safeActiveIndex}
                    onBulletClick={handleBulletClick}
                />
            )}
            </>
            )}

            {/* ListenModal - renderizzato fuori da Swiper per evitare problemi con CSS transform */}
            <ListenModal
                isOpen={listenModal.isOpen}
                onClose={() => setListenModal({ isOpen: false, artistName: null, albumName: null, ids: {}, mode: 'listen' })}
                artistName={listenModal.artistName}
                albumName={listenModal.albumName}
                ids={listenModal.ids}
                mode={listenModal.mode}
            />

            {/* Similar Loader Overlay - componente separato che si sovrappone */}
            {shouldShowSimilarLoaderOverlay && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    backgroundColor: 'rgba(0, 0, 0, 0.95)'
                }}>
                    <GeminiLoadingScreen 
                        userPrompt={displayPrompt || null}
                        bridgeMessage={displayBridgeMessage}
                        language={language}
                    />
                </div>
            )}
        </div>
    );
};

export default ShowResult;